import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { createPortal } from "react-dom";
import './hangtag-designer.css';
import Icon from './components/icon';

const t = (key, defaultText = '') => {
    const norm = String(key).trim().toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    const normRaw = String(key).trim().toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');

    if (window.htd_ajax_obj && window.htd_ajax_obj.translations) {
        if (window.htd_ajax_obj.translations[key]) {
            return window.htd_ajax_obj.translations[key];
        }
        if (window.htd_ajax_obj.translations[norm]) {
            return window.htd_ajax_obj.translations[norm];
        }
        if (window.htd_ajax_obj.translations[normRaw]) {
            return window.htd_ajax_obj.translations[normRaw];
        }
    }
    if (window.hangtagDesignerData && window.hangtagDesignerData.translations) {
        if (window.hangtagDesignerData.translations[key]) {
            return window.hangtagDesignerData.translations[key];
        }
        if (window.hangtagDesignerData.translations[norm]) {
            return window.hangtagDesignerData.translations[norm];
        }
        if (window.hangtagDesignerData.translations[normRaw]) {
            return window.hangtagDesignerData.translations[normRaw];
        }
    }
    return defaultText || key;
};

const getCurrencySymbol = () => {
    if (window.htd_ajax_obj && window.htd_ajax_obj.currency_symbol) {
        return window.htd_ajax_obj.currency_symbol;
    }
    if (window.hangtagDesignerData && window.hangtagDesignerData.currency_symbol) {
        return window.hangtagDesignerData.currency_symbol;
    }
    if (window.woocommerce_price_slider_params && window.woocommerce_price_slider_params.currency_format_symbol) {
        return window.woocommerce_price_slider_params.currency_format_symbol;
    }
    return '$';
};


// Sabitler (Options)
const materialOptions = [
    { name: 'standard', label: 'Standard', description: 'Basic cardboard material' },
    { name: 'premium', label: 'Premium', description: 'High-quality cardboard' },
    { name: 'recycled', label: 'Recycled', description: 'Eco-friendly recycled material' },
];

const applicationMethods = [
    { name: 'none', label: 'None', description: 'No attachment method', icon: '⨯' },
    { name: 'hole', label: 'Hole', description: 'Punch hole for attachment', icon: '○' },
    { name: 'string', label: 'String', description: 'String attachment', icon: '─' },
];

const stringTypeOptions = [
    { name: 'black', label: 'Black', description: 'Black string with clip', icon: '⬛' },
    { name: 'white', label: 'White', description: 'White string with clip', icon: '⬜' },
    { name: 'organic', label: 'Organic', description: 'Eco-friendly string with clip', icon: '🌿' },
];

const extraOptions = [
    { name: 'cornerRadius', label: 'Rounded Corners', description: 'Add rounded corners' },
    { name: 'glossyFinish', label: 'Glossy Finish', description: 'Add a glossy finish' },
    { name: 'reinforced', label: 'Plastering', description: 'Double the thickness' },
];

const frameStyles = [
    { name: 'none', label: 'None', description: 'No frame' },
    { name: 'single', label: 'Single', description: 'Single-line frame' },
];

// Offset multiplier table for each shape and size
const OFFSET_MULTIPLIERS = {
    rectangle: {
        small: { x: 2.02, y: 2.02 },
        medium: { x: 1.96, y: 1.96 },
        large: { x: 2.5, y: 2.5 }
    },
    square: { fixed: { x: 1.4, y: 1.3 } },
    circle: { fixed: { x: 1.4, y: 1.3 } }
};
function getOffsetMultiplier(shape, size) {
    if (shape === 'rectangle') return OFFSET_MULTIPLIERS.rectangle[size] || { x: 2, y: 2 };
    if (shape === 'square') return OFFSET_MULTIPLIERS.square.fixed;
    if (shape === 'circle') return OFFSET_MULTIPLIERS.circle.fixed;
    return { x: 2, y: 2 };
}

const HOLE_DIAMETER_MM = 2; // mm cinsinden sabit delik çapı
const HOLE_TOP_OFFSET_MM = 4; // mm cinsinden sabit delik üstten mesafe
const MM_TO_PX = 10; // 1mm = 10px (canvas'ta 50mm = 500px gibi)

// --- Yardımcı fonksiyon: Canvas'ta yuvarlatılmış dikdörtgen çiz ---
function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}


export default function hangtagdesigner() {
    // Memoized active quantities from settings
    const activeQuantities = useMemo(() => {
        const settings = (window.htd_ajax_obj && window.htd_ajax_obj.settings) || {};
        if (Array.isArray(settings.active_quantities) && settings.active_quantities.length > 0) {
            return settings.active_quantities.map(Number).sort((a, b) => a - b);
        }
        return [250, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 7000, 8000, 9000, 10000];
    }, []);

    const [design, setDesign] = useState(() => {
        const settings = (window.htd_ajax_obj && window.htd_ajax_obj.settings) || {};

        const activeQs = Array.isArray(settings.active_quantities) ? settings.active_quantities.map(Number).sort((a, b) => a - b) : [];
        const initialQty = activeQs.length > 0 ? activeQs[0] : 250;

        return {
            shape: 'rectangle',
            textA: '',
            textB: '',
            font: 'Roboto',
            fontSize: 14,
            textColor: '#000000',
            backgroundColor: '#ffffff',
            icon: 'none',
            iconPosition: 'none',
            size: 'medium',
            cardboardType: 'standard',
            applicationMethod: 'hole',
            stringType: 'black',
            frameStyle: 'none',
            extras: {
                cornerRadius: false,
                glossyFinish: false,
                reinforced: false,
            },
            quantity: initialQty,
            backside: 'blank',
            sameBack: false,
        };
    });

    const [textPositionA, setTextPositionA] = useState({ x: 0, y: 0, scale: 1, rotate: 0 });
    const [textPositionB, setTextPositionB] = useState({ x: 0, y: 0, scale: 1, rotate: 0 });
    const [imageA, setImageA] = useState(null);
    const [imageB, setImageB] = useState(null);
    const [rawImageAId, setRawImageAId] = useState(0);
    const [rawImageBId, setRawImageBId] = useState(0);
    const [uploadingA, setUploadingA] = useState(false);
    const [uploadingB, setUploadingB] = useState(false);
    const [imagePositionA, setImagePositionA] = useState({ x: 0, y: 0, scale: 1, rotate: 0 });
    const [imagePositionB, setImagePositionB] = useState({ x: 0, y: 0, scale: 1, rotate: 0 });
    const [fontCategory, setFontCategory] = useState('All');
    const [iconCategory, setIconCategory] = useState('All');
    const [loading, setLoading] = useState(false);
    const [overlay, setOverlay] = useState({ show: false, message: '', onConfirm: null });
    const [scrollPosition, setScrollPosition] = useState(0);
    const [fontScrollPosition, setFontScrollPosition] = useState(0);
    const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
    const [textEditorOpenA, setTextEditorOpenA] = useState(false);
    const [textEditorOpenB, setTextEditorOpenB] = useState(false);
    const [activeMobileSide, setActiveMobileSide] = useState('A');
    const [activeMobileTab, setActiveMobileTab] = useState('shape_size');
    const [zoomSide, setZoomSide] = useState(null);

    const categorySliderRef = useRef(null);
    const fontCategorySliderRef = useRef(null);
    const cardARef = useRef(null);
    const cardBRef = useRef(null);
    const canvasRef = useRef(null);
    const [canvasHeight, setCanvasHeight] = useState(220);

    useEffect(() => {
        if (!canvasRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (let entry of entries) {
                setCanvasHeight(entry.target.offsetHeight);
            }
        });
        observer.observe(canvasRef.current);
        return () => observer.disconnect();
    }, []);

    const fontCategories = useMemo(() => {
        // Önce orijinal kategorileri tanımla
        const baseCategories = {
            "Sans Serif": [
                "Roboto", // Temiz, modern, çok yönlü
                "Open Sans", // Okunabilir, sade
                "Montserrat", // Geometrik, şık
                "Poppins", // Yuvarlak, dostane
                "Inter", // UI için optimize, net
                "Lato", // Dengeli, profesyonel
                "Rubik", // Hafif eğlenceli, modern
                "Work Sans", // Hafif ince, çağdaş
                "Nunito", // Yuvarlak, samimi
                "Source Sans 3", // Çok yönlü, okunabilir
                "Barlow", // Modern, hafif kondense
                "Public Sans", // Resmi, temiz
            ],
            "Serif": [
                "Playfair Display", // Zarif, dekoratif
                "Merriweather", // Okunabilir, klasik
                "Lora", // Yumuşak, edebi
                "Crimson Pro", // Sofistike, tarihsel
                "Libre Baskerville", // Kitaplar için ideal
                "EB Garamond", // Rönesans esintili
                "Zilla Slab", // Modern serif, güçlü
                "Cardo", // Akademik, zarif
                "Bitter", // Kalın, modern serif
                "PT Serif", // Dengeli, klasik
                "Vollkorn", // Sağlam, tarihsel
                "Source Serif 4", // Çok yönlü, okunabilir
            ],
            "Handwriting": [
                "Caveat", // Rahat, el yazısı tarzı
                "Dancing Script", // Akıcı, zarif
                "Patrick Hand", // Samimi, düzgün
                "Indie Flower", // Çizgili, eğlenceli
                "Kalam", // Kalın, doğal
                "Shadows Into Light", // İnce, kişisel
                "Amatic SC", // El yazısı, cesur
                "Architects Daughter", // Sevimli, casual
                "Gochi Hand", // Çocukça, eğlenceli
                "Annie Use Your Telescope", // El yazısı, samimi
                "Coming Soon", // İnce, rahat
                "Nothing You Could Do", // Fırça tarzı, doğal
            ],
            "Decorative": [
                "Bebas Neue", // Kalın, dramatik
                "Oswald", // Kondense, güçlü
                "Lobster", // Retro, süslü
                "Abril Fatface", // Art deco, çarpıcı
                "Bangers", // Çizgi roman tarzı
                "Alfa Slab One", // Kalın, slab serif
                "Shrikhand", // Etnik, dekoratif
                "Fredericka the Great", // Gotik, süslü
                "Black Han Sans", // Kalın, sans-serif benzeri
                "Big Shoulders", // Geometrik, endüstriyel
                "Passion One", // Enerjik, yuvarlak
                "Rammetto One", // Eğlenceli, dekoratif
            ],
            "Modern": [
                "Futura", // Geometrik, fütüristik
                "Manrope", // Minimal, yuvarlak
                "Overpass", // Teknolojik, temiz
                "Jost", // Modern, sofistike
                "Sora", // İnce, çağdaş
                "Outfit", // Moda odaklı, zarif
                "Chivo", // Net, profesyonel
                "Karla", // Minimalist, dostane
                "Archivo", // Geometrik, modern
                "DM Sans", // Temiz, çok yönlü
                "Exo 2", // Teknolojik, yuvarlak
                "Cabin", // Modern, dengeli
            ],
            "Vintage": [
                "Special Elite", // Daktilo tarzı
                "Old Standard TT", // Antik, klasik
                "Cinzel", // Roma esintili
                "IM Fell English", // Tarihsel, el yazması
                "UnifrakturMaguntia", // Gotik, eski Alman
                "Spectral", // Retro, zarif
                "Rye", // Vahşi batı tarzı
                "Almendra", // Ortaçağ esintili
                "Pirata One", // Korsan, dekoratif
                "Metal Mania", // Gotik, heavy metal
                "Germania One", // Alman, tarihsel
                "Caesar Dressing", // Antik Roma esintili
            ],
            "Script": [
                "Great Vibes", // Zarif, kaligrafik
                "Sacramento", // İnce, akıcı
                "Alex Brush", // Romantik, fırça tarzı
                "Kaushan Script", // Enerjik, el yazısı
                "Parisienne", // Fransız, zarif
                "Yellowtail", // Modern kaligrafi
                "Tangerine", // İnce, süslü
                "Clicker Script", // Fırça, dekoratif
                "Allura", // Akıcı, zarif
                "Bad Script", // Düzensiz, el yazısı
                "Lovers Quarrel", // Romantik, süslü
                "Cedarville Cursive", // İnce, kıvrımlı
            ],
            "Monospace": [
                "Fira Code", // Kodlama için optimize
                "IBM Plex Mono", // Modern, net
                "Space Mono", // Geometrik, retro
                "Inconsolata", // Okunabilir, sade
                "Roboto Mono", // Temiz, profesyonel
                "Source Code Pro", // Adobe tasarımı, net
                "JetBrains Mono", // Programlama dostu
                "Courier Prime", // Klasik daktilo tarzı
                "Overpass Mono", // Modern, teknolojik
                "Ubuntu Mono", // Yuvarlak, okunabilir
                "VT323", // Retro, dijital
                "Share Tech Mono", // Teknolojik, net
            ],
        };

        // Tüm fontları tek bir dizide topla
        const allFonts = [...new Set(Object.values(baseCategories).flat())];

        // All kategorisini ekle ve diğer kategorilerle birleştir
        return {
            "All": allFonts,
            ...baseCategories
        };
    }, []);

    const iconCategories = useMemo(() => {
        const categories = [
            {
                name: 'Letters',
                icon: 'a',
                icons: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z']
            },
            {
                name: 'Numbers',
                icon: 'circle-number-1',
                icons: ['circle-number-0', 'circle-number-1', 'circle-number-2', 'circle-number-3', 'circle-number-4', 'circle-number-5',
                    'circle-number-6', 'circle-number-7', 'circle-number-8', 'circle-number-9', 'number-0', 'number-1', 'number-2',
                    'number-3', 'number-4', 'number-5', 'number-6', 'number-7', 'number-8', 'number-9', 'number-0-alt', 'number-1-alt',
                    'number-2-alt', 'number-3-alt', 'number-4-alt', 'number-5-alt', 'number-6-alt', 'number-7-alt', 'number-8-alt', 'number-9-alt']
            },
            {
                name: 'Arrows',
                icon: 'arrow-right',
                icons: ['arrow-down', 'arrow-down-from-line', 'arrow-down-left', 'arrow-down-right', 'arrow-down-short-wide',
                    'arrow-down-to-bracket', 'arrow-down-to-line', 'arrow-down-wide-short', 'arrow-left', 'arrow-left-arrow-right',
                    'arrow-left-from-line', 'arrow-left-to-line', 'arrow-right', 'arrow-right-from-bracket', 'arrow-right-from-line',
                    'arrow-right-to-bracket', 'arrow-right-to-line', 'arrow-rotate-left', 'arrow-rotate-right', 'arrow-trend-down',
                    'arrow-trend-up', 'arrow-turn-down-left', 'arrow-turn-down-right', 'arrow-turn-left-down', 'arrow-turn-left-up',
                    'arrow-turn-right-down', 'arrow-turn-right-up', 'arrow-turn-up-left', 'arrow-turn-up-right', 'arrow-up',
                    'arrows-left-right', 'arrows-repeat', 'arrows-rotate-clockwise', 'arrows-rotate-counter-clockwise']
            },
            {
                name: 'Shapes',
                icon: 'circle',
                icons: ['circle', 'circle-half', 'diamond', 'diamond-half', 'diamond-shape', 'hexagon', 'octagon', 'octagon-exclamation',
                    'square', 'square-checkmark', 'square-divide', 'square-equals', 'square-minus', 'square-plus', 'square-x',
                    'triangle', 'triangle-exclamation']
            },
            {
                name: 'Text Formatting',
                icon: 'align-left',
                icons: ['align-bottom', 'align-center-horizontal', 'align-center-vertical', 'align-left', 'align-right', 'align-text-center',
                    'align-text-justify', 'align-text-right', 'align-top', 'bold', 'italic', 'underline', 'cursor', 'cursor-click',
                    'grid', 'grid-masonry', 'maximize', 'minimize', 'sidebar-left', 'sidebar-right']
            },
            {
                name: 'Media',
                icon: 'microphone',
                icons: ['camera', 'camera-slash', 'desktop', 'film', 'headphones', 'image', 'images', 'laptop', 'microphone',
                    'microphone-slash', 'mobile', 'phone', 'phone-slash', 'tv', 'tv-retro', 'video', 'video-camera', 'video-camera-slash']
            },
            {
                name: 'Weather',
                icon: 'cloud',
                icons: ['cloud', 'cloud-arrow-down', 'cloud-arrow-up', 'cloud-fog', 'cloud-lightning', 'cloud-rain', 'cloud-snow',
                    'moon', 'moon-cloud', 'moon-fog', 'rainbow', 'rainbow-cloud', 'sun', 'sun-cloud', 'sun-fog', 'wind']
            },
            {
                name: 'Tools',
                icon: 'key',
                icons: ['book', 'book-open', 'bookmark', 'bookmark-plus', 'books', 'key', 'key-skeleton', 'keyboard',
                    'toolbox', 'wrench', 'pencil', 'pen-nib', 'palette', 'ruler', 'scissors']
            },
            {
                name: 'Food & Drink',
                icon: 'utensils',
                icons: ['bottle', 'cake', 'cake-slice', 'citrus-slice', 'cocktail', 'cupcake', 'ice-cream', 'mug',
                    'pizza', 'soda', 'utensils', 'wine-glass']
            },
            {
                name: 'Currency',
                icon: 'dollar',
                icons: ['british-pound', 'dollar', 'euro', 'yen', 'credit-card', 'money', 'receipt', 'wallet']
            },
            {
                name: 'Emojis',
                icon: 'face-smile',
                icons: ['face-angry', 'face-cry', 'face-laugh', 'face-meh', 'face-melt', 'face-no-mouth', 'face-open-mouth',
                    'face-sad', 'face-smile', 'person', 'person-walking', 'person-wave', 'user', 'users']
            },
            {
                name: 'Sports',
                icon: 'dice',
                icons: ['baseball', 'baseball-bat', 'basketball', 'dice', 'die-1', 'die-2', 'die-3', 'die-4', 'die-5',
                    'die-6', 'football', 'game-controller', 'hockey', 'joystick', 'soccer', 'tennis-ball']
            },
            {
                name: 'Zodiac',
                icon: 'star',
                icons: ['aquarius', 'aries', 'cancer', 'capricorn', 'gemini', 'leo', 'libra', 'pisces', 'sagittarius',
                    'scorpio', 'taurus', 'virgo', 'star', 'star-half']
            }
        ];

        const allIcons = [...new Set(categories.flatMap(cat => cat.icons))];
        return [
            {
                name: 'All',
                icon: 'star',
                icons: allIcons
            },
            ...categories
        ];
    }, []);

    const displayedIcons = useMemo(() => {
        const selectedCategory = iconCategories.find(cat => cat.name === iconCategory);
        return selectedCategory ? selectedCategory.icons : [];
    }, [iconCategory, iconCategories]);

    const hasAnyText = useMemo(() => {
        const a = (design.textA || '').trim() !== '';
        const b = design.backside === 'different' ? ((design.textB || '').trim() !== '') : false;
        return a || b;
    }, [design.textA, design.textB, design.backside]);

    const shapeOptions = [
        {
            name: 'Rectangle',
            value: 'rectangle',
            dimensions: 'Standard size', // Boyut bilgisi
            info: 'Standard hangtag shape' // Açıklama
        },
        {
            name: 'Square',
            value: 'square',
            dimensions: '50x50mm',
            info: 'Fixed size'
        },
        {
            name: 'Circle',
            value: 'circle',
            dimensions: '50mm diameter',
            info: 'Fixed size'
        }
    ];

    const backsideOptions = [
        {
            name: 'Blank',
            value: 'blank',
            info: 'Plain back with no design',
            icon: '□'
        },
        {
            name: 'Same as Front',
            value: 'same',
            info: 'Use the same design as front',
            icon: '⟲'
        },
        {
            name: 'Different',
            value: 'different',
            info: 'Create a different design',
            icon: '⟷'
        }
    ];

    const sizeOptions = useMemo(() => [
        { name: 'Small', width: 40, height: 70, label: 'Small\n40x70mm', info: 'Best for small items' },
        { name: 'Medium', width: 50, height: 90, label: 'Medium\n50x90mm', info: 'Standard size' },
        { name: 'Large', width: 70, height: 120, label: 'Large\n70x120mm', info: 'Best for large items' },
        { name: 'Fixed', width: 50, height: 50, label: 'Fixed\n50x50mm', info: 'For square and circle shapes' },
    ], []);

    const iconPositions = useMemo(() => [
        { value: 'none', label: 'No Icon', preview: 'Text' },
        { value: 'left', label: 'Left Icon', preview: '★ Text' },
        { value: 'both', label: 'Both Sides', preview: '★ Text ★' },
        { value: 'right', label: 'Right Icon', preview: 'Text ★' },
    ], []);

    const colorPalette = useMemo(() => [
        "#000000", "#FFFFFF", "#FF0000", "#FF3333", "#FF6666", "#FF9999", "#FFCCCC",
        "#FF4500", "#FF5E1A", "#FF7733", "#FF914D", "#FFAA66", "#FFD700", "#FFDF33",
        "#FFE566", "#FFEB99", "#FFF1CC", "#008000", "#1A8C1A", "#33A133", "#4DB54D",
        "#66C966", "#0000FF", "#3333FF", "#6666FF", "#9999FF", "#CCCCFF", "#800080",
        "#8C1A8C", "#A133A1", "#B54DB5", "#C966C9", "#FF69B4", "#FF7ABC", "#FF8CC4",
        "#FF9ECC", "#FFB0D4", "#333333", "#4D4D4D", "#666666", "#808080", "#999999",
        "#8B4513", "#A0522D", "#B5651D", "#C97828", "#DB8B33", "#00CED1", "#1AD4D7",
        "#33DADD", "#4DE0E3", "#66E6E9", "#4169E1", "#5277E5", "#6385E9", "#7493ED",
        "#85A1F1", "#32CD32", "#43D143", "#54D554", "#65D965", "#76DD76", "#2F4F4F",
        "#3B5A5A", "#476565", "#537070", "#5F7B7B", "#FFB6C1", "#FFDBC1", "#FFFACD",
        "#E6E6FA", "#D3F9D8",
    ], []);

    const scrollCategories = useCallback((direction) => {
        const container = categorySliderRef.current;
        if (container) {
            const scrollAmount = 200;
            const newPosition = direction === 'left' ? scrollPosition - scrollAmount : scrollPosition + scrollAmount;
            container.scrollTo({ left: newPosition, behavior: 'smooth' });
            setScrollPosition(newPosition);
        }
    }, [scrollPosition]);

    const scrollFontCategories = useCallback((direction) => {
        const container = fontCategorySliderRef.current;
        if (container) {
            const scrollAmount = 200;
            const newPosition = direction === 'left' ? fontScrollPosition - scrollAmount : fontScrollPosition + scrollAmount;
            container.scrollTo({ left: newPosition, behavior: 'smooth' });
            setFontScrollPosition(newPosition);
        }
    }, [fontScrollPosition]);

    const calculateScaleFactor = useCallback(() => {
        const checkTablet = window.innerWidth > 767 && window.innerWidth <= 1200;
        const checkMobile = window.innerWidth <= 767;
        const checkAnyResponsive = checkMobile || checkTablet;

        let maxCanvasWidth = 360;
        let maxCanvasHeight = 480;

        if (checkMobile) {
            maxCanvasWidth = Math.min(220, window.innerWidth - 48);
            maxCanvasHeight = 180;
        } else if (checkTablet) {
            maxCanvasWidth = Math.min(280, window.innerWidth - 64);
            maxCanvasHeight = 280;
        } else if (design.backside !== 'blank') {
            maxCanvasWidth = 200;
        }

        const size = sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed');

        if (design.shape === 'square' || design.shape === 'circle') {
            const baseScale = Math.min(maxCanvasWidth / 50, maxCanvasHeight / 50);
            if (checkMobile) {
                return baseScale * 1.05;
            } else if (checkTablet) {
                return baseScale * 0.8; // Enlarge Circle and Square on iPad
            } else {
                return baseScale * 1.0;
            }
        }

        // For Rectangles:
        let baseScale = 1;
        const isDoubleSidedDesktop = !checkAnyResponsive && design.backside !== 'blank';
        switch (design.size) {
            case 'small':
                baseScale = checkAnyResponsive ? 0.72 : 0.8;
                break;
            case 'medium':
                baseScale = checkAnyResponsive ? 0.85 : (isDoubleSidedDesktop ? 0.9 : 1.1);
                break;
            case 'large':
                baseScale = checkAnyResponsive ? 0.95 : (isDoubleSidedDesktop ? 1.0 : 1.4);
                break;
            default:
                baseScale = 1;
        }

        const widthScale = (maxCanvasWidth / size.width) * baseScale;
        const heightScale = (maxCanvasHeight / size.height) * baseScale;
        return Math.min(widthScale, heightScale);
    }, [design.shape, design.size, design.backside, sizeOptions]);

    const calculatePrice = useCallback(() => {
        if (!window.htd_ajax_obj || !window.htd_ajax_obj.settings) {
            return { total: '0.00', perPiece: '0.00', discountRate: 0 };
        }

        const settings = window.htd_ajax_obj.settings;
        const safeNumber = (value) => (isNaN(Number(value)) ? 0 : Number(value));

        let basePrice = 0;

        // Shape'e göre base price belirleme
        switch (design.shape) {
            case 'rectangle':
                // Dikdörtgen için boyut fiyatı
                switch (design.size.toLowerCase()) {
                    case 'small':
                        basePrice = safeNumber(settings.price_small);
                        break;
                    case 'medium':
                        basePrice = safeNumber(settings.price_medium);
                        break;
                    case 'large':
                        basePrice = safeNumber(settings.price_large);
                        break;
                    default:
                        basePrice = safeNumber(settings.price_medium);
                }
                break;
            case 'square':
                // Square için sabit fiyat
                basePrice = safeNumber(settings.price_square || settings.price_medium * 1.2);
                break;
            case 'circle':
                // Circle için sabit fiyat
                basePrice = safeNumber(settings.price_circle || settings.price_medium * 1.2);
                break;
            default:
                basePrice = safeNumber(settings.price_medium);
        }

        // Sıvama (reinforced) seçildiğinde ebat/şekil fiyatlarını 2'ye katla
        if (design.extras.reinforced) {
            basePrice = basePrice * 2;
        }

        // Materyal ücreti
        switch (design.cardboardType.toLowerCase()) {
            case 'standard':
                basePrice += safeNumber(settings.standard_cardboard_fee);
                break;
            case 'premium':
                basePrice += safeNumber(settings.premium_cardboard_fee);
                break;
            case 'recycled':
                basePrice += safeNumber(settings.recycled_cardboard_fee);
                break;
            default:
                break;
        }

        // Delik/İp ücreti
        if (design.applicationMethod === 'string') {
            const stringType = design.stringType ? design.stringType.toLowerCase() : 'black';
            if (stringType === 'black') {
                basePrice += safeNumber(settings.string_black_fee);
            } else if (stringType === 'white') {
                basePrice += safeNumber(settings.string_white_fee);
            } else if (stringType === 'organic') {
                basePrice += safeNumber(settings.string_organic_fee);
            }
        }

        // Ekstra özellikler
        if (design.extras.cornerRadius && design.shape === 'rectangle') {
            basePrice += (settings.corner_radius_fee !== undefined && settings.corner_radius_fee !== '') ? safeNumber(settings.corner_radius_fee) : 0.10;
        }
        if (design.extras.glossyFinish) {
            basePrice += safeNumber(settings.glossy_finish_fee);
        }

        // Arka yüz ücreti
        if (design.backside === 'same') {
            basePrice += (settings.same_back_fee !== undefined && settings.same_back_fee !== '') ? safeNumber(settings.same_back_fee) : 0.10;
        } else if (design.backside === 'different') {
            basePrice += (settings.different_back_fee !== undefined && settings.different_back_fee !== '') ? safeNumber(settings.different_back_fee) : 0.50;
        }

        // Calculate total price before discount
        const totalBeforeDiscount = basePrice * design.quantity;

        // Miktar indirimi - total price üzerinden uygula
        const parseDiscountOrMarkup = (value) => {
            if (value === undefined || value === null) return { rate: 0, isMarkup: false };
            const strVal = String(value).trim();
            const markup = strVal.startsWith('+');
            const numStr = strVal.replace(/[^\d.]/g, '');
            const rate = parseFloat(numStr);
            return {
                rate: isNaN(rate) ? 0 : rate,
                isMarkup: markup
            };
        };

        const getDiscountData = () => {
            const quantities = [...activeQuantities].sort((a, b) => b - a);
            for (let qty of quantities) {
                if (design.quantity >= qty) {
                    const discountKey = `quantity_${qty}_discount`;
                    return parseDiscountOrMarkup(settings[discountKey]);
                }
            }
            return { rate: 0, isMarkup: false };
        };

        const discountData = getDiscountData();
        const discountRate = discountData.rate;
        const isMarkup = discountData.isMarkup;
        const discountMultiplier = isMarkup ? (100 + discountRate) / 100 : (100 - discountRate) / 100;
        const finalTotal = totalBeforeDiscount * discountMultiplier;
        const finalPricePerPiece = finalTotal / design.quantity;

        return {
            total: finalTotal.toFixed(2),
            perPiece: finalPricePerPiece.toFixed(2),
            discountRate,
            isMarkup,
        };
    }, [design]);

    const cardPreviewStyle = (size, scale, shape, isLightbox = false) => {
        const checkTablet = window.innerWidth > 767 && window.innerWidth <= 1200;
        const checkMobile = window.innerWidth <= 767;

        let activeScale = scale;
        let activeSize = size;
        let activeShape = shape;

        if (isLightbox) {
            const largeSizeOpt = sizeOptions.find((s) => s.name.toLowerCase() === 'large') || { width: 70, height: 120 };
            activeSize = (shape === 'square' || shape === 'circle') ? { width: 50, height: 50 } : largeSizeOpt;

            if (checkMobile) {
                activeScale = 4.0;
            } else if (checkTablet) {
                activeScale = 5.0;
            } else {
                activeScale = (shape === 'rectangle') ? 2.5 : 6.0;
            }
        }

        const isCircle = activeShape === 'circle';
        const isSquare = activeShape === 'square';
        const baseWidth = (isSquare || isCircle) ? 50 : activeSize.width;
        const baseHeight = (isSquare || isCircle) ? 50 : activeSize.height;

        // Size-based scaling adjustment
        let sizeScale;
        if (isLightbox) {
            sizeScale = { width: 1.05, height: 1.05 }; // Use 'large' dimensions base multiplier
        } else {
            switch (design.size) {
                case 'small':
                    sizeScale = {
                        width: 0.90,
                        height: 0.90
                    };
                    break;
                case 'medium':
                    sizeScale = {
                        width: 0.95,
                        height: 0.95
                    };
                    break;
                case 'large':
                    sizeScale = {
                        width: 1.05,
                        height: 1.05
                    };
                    break;
                default:
                    sizeScale = {
                        width: 1,
                        height: 1
                    };
            }
        }

        // Calculate dimensions with separate scaling for width and height
        let width = baseWidth * activeScale * sizeScale.width;
        let height = baseHeight * activeScale * sizeScale.height;

        // Maximum dimensions
        const maxWidth = isLightbox ? 1200 : 360;
        const maxHeight = isLightbox ? 1200 : 480;

        // Maintain aspect ratio while fitting within bounds
        const aspectRatio = width / height;
        if (width > maxWidth) {
            width = maxWidth;
            height = width / aspectRatio;
        }
        if (height > maxHeight) {
            height = maxHeight;
            width = height * aspectRatio;
        }

        // Extra padding for square and circle
        const extraPadding = (isSquare || isCircle) ? 10 : 0;

        // Apply rounded corners for square shape if enabled
        const borderRadius = isCircle ? '50%' : (design.extras.cornerRadius ? '8px' : '0');

        return {
            width: `${Math.round(width + extraPadding)}px`,
            height: `${Math.round(height + extraPadding)}px`,
            maxWidth: `${maxWidth}px`,
            maxHeight: `${maxHeight}px`,
            borderRadius,
            backgroundColor: design.backgroundColor,
            position: 'relative',
            overflow: 'hidden',
            margin: 'auto',
            transform: isLightbox ? `scale(1)` : `scale(${sizeScale.height})`,
            transformOrigin: 'center center'
        };
    };

    // Capture fonksiyonu - HTML2Canvas için görüntü oluşturma
    const captureHangtag = async (side = 'A', previewWidth, previewHeight) => {
        //console.log(`[DEBUG] Starting hangtag capture for Side ${side}`);

        try {
            // Validate inputs - log warning but proceed since we calculate pre-transform CSS dimensions dynamically anyway
            if (!previewWidth || !previewHeight) {
                console.warn('[WARNING] Invalid preview dimensions in captureHangtag, proceeding with calculated dimensions');
            }

            // Design properties
            const isA = side.toUpperCase() === 'A';
            const text = isA ? design.textA : design.textB;
            const textPosition = isA ? textPositionA : textPositionB;
            const uploadedImage = isA ? imageA : imageB;
            const imagePosition = isA ? imagePositionA : imagePositionB;

            // Canvas setup
            // High-resolution Canvas setup (3x scale targeting ~2100-3000px resolution)
            const renderScale = 3;
            let canvasWidth, canvasHeight;
            if (design.shape === 'square' || design.shape === 'circle') {
                canvasWidth = canvasHeight = 500 * renderScale; // 1500px
            } else {
                switch (design.size.toLowerCase()) {
                    case 'small': canvasWidth = 400 * renderScale; canvasHeight = 700 * renderScale; break; // 1200x2100
                    case 'medium': canvasWidth = 500 * renderScale; canvasHeight = 900 * renderScale; break; // 1500x2700
                    case 'large': canvasWidth = 700 * renderScale; canvasHeight = 1200 * renderScale; break; // 2100x3600
                    default: canvasWidth = 500 * renderScale; canvasHeight = 900 * renderScale;
                }
            }

            const canvas = document.createElement('canvas');
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // --- CALCULATE EXACT PRE-TRANSFORM CSS DIMENSIONS ---
            const size = sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed');
            const previewScale = calculateScaleFactor();
            const isCircle = design.shape === 'circle';
            const isSquare = design.shape === 'square';
            const baseWidth = (isSquare || isCircle) ? 50 : size.width;
            const baseHeight = (isSquare || isCircle) ? 50 : size.height;

            let sizeScale;
            switch (design.size) {
                case 'small': sizeScale = { width: 0.90, height: 0.90 }; break;
                case 'medium': sizeScale = { width: 0.95, height: 0.95 }; break;
                case 'large': sizeScale = { width: 1.05, height: 1.05 }; break;
                default: sizeScale = { width: 1, height: 1 };
            }

            let cssWidth = baseWidth * previewScale * sizeScale.width;
            let cssHeight = baseHeight * previewScale * sizeScale.height;

            const maxWidth = 360;
            const maxHeight = 480;
            const aspectRatio = cssWidth / cssHeight;
            if (cssWidth > maxWidth) {
                cssWidth = maxWidth;
                cssHeight = cssWidth / aspectRatio;
            }
            if (cssHeight > maxHeight) {
                cssHeight = maxHeight;
                cssWidth = cssHeight * aspectRatio;
            }

            // Precise scaling factor from CSS preview container to canvas coordinates
            const scaleFactor = {
                width: canvasWidth / cssWidth,
                height: canvasHeight / cssHeight
            };

            const scaleX = scaleFactor.width;
            const scaleY = scaleFactor.height;

            // Shape clipping
            if (design.shape === 'circle') {
                ctx.beginPath();
                ctx.arc(canvasWidth / 2, canvasHeight / 2, Math.min(canvasWidth, canvasHeight) / 2, 0, Math.PI * 2);
                ctx.clip();
            } else if (design.extras.cornerRadius && design.shape === 'rectangle') {
                const radius = 8 * scaleFactor.width; // Match CSS 8px
                ctx.beginPath();
                ctx.moveTo(radius, 0);
                ctx.lineTo(canvasWidth - radius, 0);
                ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, radius);
                ctx.lineTo(canvasWidth, canvasHeight - radius);
                ctx.quadraticCurveTo(canvasWidth, canvasHeight, canvasWidth - radius, canvasHeight);
                ctx.lineTo(radius, canvasHeight);
                ctx.quadraticCurveTo(0, canvasHeight, 0, canvasHeight - radius);
                ctx.lineTo(0, radius);
                ctx.quadraticCurveTo(0, 0, radius, 0);
                ctx.closePath();
                ctx.clip();
            }

            // Background
            ctx.fillStyle = design.backgroundColor;
            ctx.fillRect(0, 0, canvasWidth, canvasHeight);

            // Frame
            if (design.frameStyle === 'single') {
                const margin = Math.max(1, 8 * scaleFactor.width);
                const thickness = Math.max(1, 2 * scaleFactor.width);
                const w = Math.max(1, canvasWidth - 2 * margin);
                const h = Math.max(1, canvasHeight - 2 * margin);
                const radius = design.extras.cornerRadius ? Math.min(8 * scaleFactor.width, w / 2, h / 2) : 0;
                ctx.save();
                ctx.lineWidth = thickness;
                ctx.strokeStyle = design.textColor;
                try {
                    if (design.shape === 'circle') {
                        ctx.beginPath();
                        ctx.arc(canvasWidth / 2, canvasHeight / 2, (canvasWidth / 2) - margin, 0, Math.PI * 2);
                        ctx.stroke();
                    } else if (radius > 0) {
                        roundedRect(ctx, margin, margin, w, h, radius);
                        ctx.stroke();
                    } else {
                        ctx.strokeRect(margin, margin, w, h);
                    }
                } catch (e) {
                    console.error('[FRAME ERROR]', e, { canvasWidth, canvasHeight, scaleFactor, margin, thickness, w, h, radius });
                }
                ctx.restore();
            }
            const { verticalPadding, horizontalPadding, verticalFrameOffset, horizontalFrameOffset } = calculateFrameOffsets();

            // Application method (hole position for text alignment, scaled by renderScale)
            const holeDiameterPx = HOLE_DIAMETER_MM * MM_TO_PX * renderScale;
            const holeTopOffsetPx = HOLE_TOP_OFFSET_MM * MM_TO_PX * renderScale;
            if (design.applicationMethod === 'hole') {
                ctx.fillStyle = '#ffffff';
                ctx.beginPath();
                ctx.arc(canvasWidth / 2, holeTopOffsetPx, holeDiameterPx / 2, 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = '#dddddd';
                ctx.lineWidth = 2 * scaleFactor.width;
                ctx.stroke();
            }

            // Center reference
            const centerX = canvasWidth / 2;
            const centerY = canvasHeight / 2;

            // Image rendering
            if (uploadedImage) {
                const img = new Image();
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = uploadedImage;
                });

                // Proportional Object-Fit: Contain logic inside the canvas boundaries
                const imageRatio = img.width / img.height;
                const canvasRatio = canvasWidth / canvasHeight;
                const userScale = imagePosition.scale || 1;

                let drawWidth, drawHeight;
                if (imageRatio > canvasRatio) {
                    // Width-limited aspect ratio containment
                    drawWidth = canvasWidth * userScale;
                    drawHeight = (canvasWidth / imageRatio) * userScale;
                } else {
                    // Height-limited aspect ratio containment
                    drawHeight = canvasHeight * userScale;
                    drawWidth = (canvasHeight * imageRatio) * userScale;
                }

                // Dynamic, mathematically exact translation offsets
                const offsetX = (imagePosition.x || 0) * scaleX;
                const offsetY = (imagePosition.y || 0) * scaleY;

                // Center coordinates
                const imgX = canvasWidth / 2 + offsetX;
                const imgY = canvasHeight / 2 + offsetY;
                const imgRotation = imagePosition.rotate || 0;

                ctx.save();
                ctx.translate(imgX, imgY);
                ctx.rotate(imgRotation * Math.PI / 180);
                ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
                ctx.restore();
            }

            // Text rendering
            if (text) {
                const userScale = textPosition.scale || 1;
                const textRotation = textPosition.rotate || 0;

                // Font size: scale by scaleY and user scale
                const canvasFontSize = design.fontSize * scaleY * userScale;
                ctx.font = `${canvasFontSize}px ${design.font || 'Arial'}`;
                ctx.fillStyle = design.textColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle'; // center reference

                // Dynamic, mathematically exact translation offsets
                const textOffsetX = (textPosition.x || 0) * scaleX;
                const textOffsetY = (textPosition.y || 0) * scaleY;

                // Position: center of canvas + offset
                const textX = canvasWidth / 2 + textOffsetX;
                const textY = canvasHeight / 2 + textOffsetY;

                ctx.save();
                ctx.translate(textX, textY);
                ctx.rotate(textRotation * Math.PI / 180);

                // Split text into lines and draw each line
                const lines = text.split('\n');
                const lineHeight = canvasFontSize * 1.2;
                lines.forEach((line, index) => {
                    const y = (index - (lines.length - 1) / 2) * lineHeight;
                    ctx.fillText(line, 0, y);
                });

                ctx.restore();
            }

            return canvas;
        } catch (error) {
            console.error(`[ERROR] Error in captureHangtag for Side ${side}:`, error);
            return null;
        }
    };

    const base64ToHex = (base64Str) => {
        if (!base64Str) return '';
        try {
            const commaIdx = base64Str.indexOf(',');
            const cleanStr = commaIdx !== -1 ? base64Str.substring(commaIdx + 1) : base64Str;
            const raw = window.atob(cleanStr);
            let result = '';
            for (let i = 0; i < raw.length; i++) {
                const hex = raw.charCodeAt(i).toString(16);
                result += (hex.length === 2 ? hex : '0' + hex);
            }
            return 'hex:' + result;
        } catch (e) {
            console.error('base64ToHex error:', e);
            return base64Str;
        }
    };

    const compressImage = (base64Str, maxWidth = 2400, maxHeight = 2400) => {
        if (!base64Str) return Promise.resolve('');
        if (base64Str.startsWith('data:image/svg+xml;base64,')) {
            return Promise.resolve(base64Str);
        }
        return new Promise((resolve) => {
            const img = new Image();
            img.src = base64Str;
            img.onload = () => {
                let width = img.width;
                let height = img.height;
                if (width <= maxWidth && height <= maxHeight) {
                    return resolve(base64Str);
                }
                if (width > maxWidth || height > maxHeight) {
                    if (width > height) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    } else {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                ctx.drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(base64Str);
        });
    };

    const base64ToBlob = (base64Data, contentType) => {
        contentType = contentType || '';
        const sliceSize = 1024;
        const byteCharacters = atob(base64Data.split(',')[1] || base64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
        return new Blob(byteArrays, { type: contentType });
    };

    const addToCart = async () => {
        try {
            //console.log('[DEBUG] AddToCart function started');
            setLoading(true);
            await new Promise(resolve => setTimeout(resolve, 100));

            // Metin veya dosya kontrolü
            const hasTextA = design.textA && design.textA.trim() !== '';
            const hasImageA = imageA !== null;
            const hasContentA = hasTextA || hasImageA;

            const hasTextB = design.textB && design.textB.trim() !== '';
            const hasImageB = imageB !== null;
            const hasContentB = hasTextB || hasImageB;

            // Arka yüz kontrolü
            if (design.backside === 'different' && !hasContentB) {
                alert(t('please_enter_text_or_upload_an_image_for_side_b_before_adding_to_cart', 'Please enter text or upload an image for Side B before adding to cart.'));
                setLoading(false);
                return;
            }

            // Ön yüz kontrolü
            if (!hasContentA) {
                alert(t('please_enter_text_or_upload_an_image_for_side_a_before_adding_to_cart', 'Please enter text or upload an image for Side A before adding to cart.'));
                setLoading(false);
                return;
            }

            // Fiyat bilgilerini al
            const priceInfo = calculatePrice();
            //console.log('[DEBUG] Price Info:', priceInfo);

            // Etiketleri yakala
            //console.log('[DEBUG] Capturing hangtags...');
            //console.log('[DEBUG] Current text positions - A:', JSON.stringify(textPositionA), 'B:', JSON.stringify(textPositionB));
            //console.log('[DEBUG] Current image positions - A:', JSON.stringify(imagePositionA), 'B:', JSON.stringify(imagePositionB));

            // A ve B tarafı görüntülerini oluştur
            let canvasA = null;
            let canvasB = null;

            // Safe, cross-fallback bounding dimensions extraction for mobile hidden previews
            const previewRectA = cardARef.current ? cardARef.current.getBoundingClientRect() : null;
            const previewRectB = cardBRef.current ? cardBRef.current.getBoundingClientRect() : null;

            let previewWidthA = previewRectA ? previewRectA.width : 0;
            let previewHeightA = previewRectA ? previewRectA.height : 0;
            let previewWidthB = previewRectB ? previewRectB.width : 0;
            let previewHeightB = previewRectB ? previewRectB.height : 0;

            // Fallback between Side A and Side B if either is currently hidden (e.g. display: none tab)
            if (!previewWidthA || !previewHeightA) {
                previewWidthA = previewWidthB;
                previewHeightA = previewHeightB;
            }
            if (!previewWidthB || !previewHeightB) {
                previewWidthB = previewWidthA;
                previewHeightB = previewHeightA;
            }

            // Sensible fallback value in case both are somehow 0 (unmounted or fully hidden)
            if (!previewWidthA || !previewHeightA) {
                previewWidthA = 200;
                previewHeightA = 360;
            }
            if (!previewWidthB || !previewHeightB) {
                previewWidthB = 200;
                previewHeightB = 360;
            }

            try {
                //console.log('[DEBUG] Attempting to capture Side A');
                canvasA = await captureHangtag('A', previewWidthA, previewHeightA);
                if (!canvasA) {
                    throw new Error('Failed to capture Side A');
                }
                //console.log('[DEBUG] Side A capture successful');
            } catch (errorA) {
                console.error('[ERROR] Failed to capture Side A:', errorA);
                setLoading(false);
                alert(t('error_capturing_side_a_image_please_try_again', 'Error capturing Side A image. Please try again.'));
                return;
            }

            if (design.backside === 'different') {
                try {
                    //console.log('[DEBUG] Attempting to capture Side B');
                    canvasB = await captureHangtag('B', previewWidthB, previewHeightB);
                    if (!canvasB) {
                        throw new Error('Failed to capture Side B');
                    }
                    //console.log('[DEBUG] Side B capture successful');
                } catch (errorB) {
                    console.error('[ERROR] Failed to capture Side B:', errorB);
                    setLoading(false);
                    alert(t('error_capturing_side_b_image_please_try_again', 'Error capturing Side B image. Please try again.'));
                    return;
                }
            }

            //console.log('[DEBUG] Converting canvas to data URLs');
            // Canvas'ları base64 data URL'lerine dönüştür
            let sideAImage;
            let sideBImage = null;

            try {
                sideAImage = canvasA.toDataURL('image/png');
                //console.log('[DEBUG] Side A image converted to data URL', sideAImage.substring(0, 50) + '...');

                if (canvasB) {
                    sideBImage = canvasB.toDataURL('image/png');
                    //console.log('[DEBUG] Side B image converted to data URL', sideBImage.substring(0, 50) + '...');
                }
            } catch (e) {
                console.error('[ERROR] Error converting canvas to data URL:', e);
                setLoading(false);
                alert(t('error_processing_image_please_try_again', 'Error processing image. Please try again.'));
                return;
            }

            // Tasarım verilerine pozisyon bilgilerini ekle
            const enhancedDesignData = {
                ...design,
                textPositionA: textPositionA,
                textPositionB: textPositionB,
                imagePositionA: imagePositionA,
                imagePositionB: imagePositionB
            };

            let compressedSideA;
            let compressedSideB = '';
            let compressedImageA = '';
            let compressedImageB = '';

            try {
                compressedSideA = await compressImage(sideAImage, 2400, 2400);
                if (sideBImage) {
                    compressedSideB = await compressImage(sideBImage, 2400, 2400);
                }
                if (imageA) {
                    compressedImageA = await compressImage(imageA, 2000, 2000);
                }
                if (imageB) {
                    compressedImageB = await compressImage(imageB, 2000, 2000);
                }
            } catch (e) {
                console.error('[ERROR] Error compressing images:', e);
                compressedSideA = sideAImage;
                compressedSideB = sideBImage || '';
                compressedImageA = imageA || '';
                compressedImageB = imageB || '';
            }

            let postParams;
            try {
                postParams = new URLSearchParams({
                    action: 'add_to_cart_custom_hangtag',
                    security: (window.hangtagDesignerData && window.hangtagDesignerData.nonce) || '',
                    total_price: priceInfo.total,
                    price_per_piece: priceInfo.perPiece,
                    quantity: design.quantity,
                    design_data: JSON.stringify(enhancedDesignData),
                    side_a_image: base64ToHex(compressedSideA),
                    side_b_image: compressedSideB ? base64ToHex(compressedSideB) : '',
                    raw_image_a: compressedImageA ? base64ToHex(compressedImageA) : '',
                    raw_image_b: compressedImageB ? base64ToHex(compressedImageB) : '',
                    raw_image_a_id: rawImageAId || 0,
                    raw_image_b_id: rawImageBId || 0
                });
            } catch (e) {
                console.error('[ERROR] Error preparing data:', e);
                setLoading(false);
                alert(t('error_preparing_data_please_try_again', 'Error preparing data. Please try again.'));
                return;
            }

            // AJAX URL'i kontrol et
            if (!window.hangtagDesignerData || !window.hangtagDesignerData.ajaxUrl) {
                console.error('[ERROR] AJAX URL is not defined');
                setLoading(false);
                alert(t('error_system_configuration_issue_please_contact_support', 'Error: System configuration issue. Please contact support.'));
                return;
            }

            // AJAX isteği gönder
            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 saniye timeout

                const targetUrl = window.hangtagDesignerData.rest_add_to_cart || window.hangtagDesignerData.ajaxUrl;
                const response = await fetch(targetUrl, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded'
                    },
                    body: postParams,
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    throw new Error(`Server returned ${response.status}: ${response.statusText}`);
                }

                //console.log('[DEBUG] Response received', response);

                // Response'u text olarak al
                const responseText = await response.text();
                //console.log('[DEBUG] Response text received', responseText.substring(0, 100) + '...');

                // İlk geçerli JSON'ı bul
                const jsonMatch = responseText.match(/\{.*\}/s);
                if (!jsonMatch) {
                    console.error('[ERROR] Invalid server response - no JSON found');
                    throw new Error('Invalid server response');
                }

                // JSON parse et
                const result = JSON.parse(jsonMatch[0]);
                //console.log('[DEBUG] Parsed result:', result);

                if (!result.success) {
                    console.error('[ERROR] Server reported failure:', result);
                    throw new Error((result.data && result.data.message) || 'Failed to add to cart');
                }

                // Başarılı ise doğrudan sepete yönlendir
                window.location.href = result.data.cart_url;
            } catch (error) {
                console.error('[ERROR] AJAX request failed:', error);
                setLoading(false);

                // Özel hata mesajları
                if (error.name === 'AbortError') {
                    alert(t('the_request_took_too_long_to_complete_please_try_again', 'The request took too long to complete. Please try again.'));
                } else {
                    alert(t('error_adding_to_cart_detailed', 'Error adding to cart: ') + error.message);
                }
            }
        } catch (error) {
            console.error('[ERROR] Add to cart error:', error);
            alert(t('error_adding_to_cart_detailed', 'Error adding to cart: ') + error.message);
            setLoading(false);
        }
    };

    const calculateFrameOffsets = useCallback(() => {
        const size = sizeOptions.find(s => s.name.toLowerCase() === design.size) || sizeOptions.find(s => s.name === 'Fixed');
        const verticalPadding = Math.max(2, size.height * 0.02);
        const horizontalPadding = Math.max(4, size.width * 0.03);
        const verticalFrameOffset = design.frameStyle === 'single' ? Math.max(6, size.height * 0.04) : verticalPadding;
        const horizontalFrameOffset = design.frameStyle === 'single' ? Math.max(6, size.width * 0.04) : horizontalPadding;
        return { verticalPadding, horizontalPadding, verticalFrameOffset, horizontalFrameOffset };
    }, [design.frameStyle, design.size, sizeOptions]);

    const handleImageUpload = (side, event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if the file is a PNG
            const isPNG = file.type === 'image/png';

            // Create a new image to check dimensions and handle transparency
            const img = new Image();
            const reader = new FileReader();

            reader.onload = (e) => {
                img.onload = () => {
                    // Create a canvas to handle the image
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Set canvas dimensions to match image
                    canvas.width = img.width;
                    canvas.height = img.height;

                    // Clear canvas with transparent background
                    ctx.clearRect(0, 0, canvas.width, canvas.height);

                    // Draw the image
                    ctx.drawImage(img, 0, 0);

                    // Get the image data as PNG to preserve transparency
                    const imageData = canvas.toDataURL('image/png');

                    // Update the state with the processed image
                    if (side === 'A') {
                        setImageA(imageData);
                    } else {
                        setImageB(imageData);
                    }
                };

                img.src = e.target.result;
            };

            // Read the file as data URL
            reader.readAsDataURL(file);

            // Immediate background upload to WordPress Media Library
            const uploadFile = async () => {
                if (side === 'A') {
                    setUploadingA(true);
                } else {
                    setUploadingB(true);
                }

                try {
                    const restUrl = (window.htd_ajax_obj && window.htd_ajax_obj.rest_upload_logo) || '';
                    const ajaxUrl = (window.htd_ajax_obj && window.htd_ajax_obj.ajax_url) || '';
                    const nonce = (window.htd_ajax_obj && window.htd_ajax_obj.nonce) || '';

                    if (!restUrl && !ajaxUrl) return;

                    const readAsBase64Promise = (f) => {
                        return new Promise((resolve, reject) => {
                            const rd = new FileReader();
                            rd.onload = () => resolve(rd.result);
                            rd.onerror = reject;
                            rd.readAsDataURL(f);
                        });
                    };

                    let base64 = await readAsBase64Promise(file);
                    if (file.type && file.type.startsWith('image/') && !file.type.includes('svg')) {
                        try {
                            base64 = await compressImage(base64, 1000, 1000);
                        } catch (err) {
                            console.warn('TagStudio: compress image error:', err);
                        }
                    }

                    const hex = base64ToHex(base64);
                    const formData = new URLSearchParams({
                        action: 'htd_upload_logo',
                        security: nonce,
                        nonce: nonce,
                        hex_file: hex,
                        filename: file.name
                    });

                    const response = await axios.post(
                        restUrl || ajaxUrl,
                        formData,
                        {
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                        }
                    );

                    if (response.data && response.data.success) {
                        const attachment = response.data.data;
                        if (side === 'A') {
                            setRawImageAId(attachment.id);
                        } else {
                            setRawImageBId(attachment.id);
                        }
                        console.log(`TagStudio: Raw image ${side} pre-uploaded successfully. ID:`, attachment.id);
                    } else {
                        console.error('TagStudio: Upload failed:', response.data);
                    }
                } catch (err) {
                    console.error('TagStudio: Error uploading raw logo:', err);
                } finally {
                    if (side === 'A') {
                        setUploadingA(false);
                    } else {
                        setUploadingB(false);
                    }
                }
            };

            uploadFile();
        }
    };

    const handleShapeChange = (shape) => {
        setDesign((prev) => ({
            ...prev,
            shape: shape.toLowerCase(),
            size: shape === 'rectangle' ? 'medium' : 'fixed'
        }));
    };

    const handleBacksideChange = (value) => {
        setDesign(prev => {
            // Same'den different'a geçerken textB'yi kopyalama!
            if (prev.backside === 'same' && value === 'different') {
                return {
                    ...prev,
                    backside: value,
                    sameBack: false,
                    // textB'yi boş bırak
                    textB: '',
                    textPositionB: { x: 0, y: 0, scale: 1, rotate: 0 },
                    imageB: null,
                    imagePositionB: { x: 0, y: 0, scale: 1, rotate: 0 },
                    stringType: prev.applicationMethod === 'string' ? prev.stringType : 'black',
                };
            }
            return {
                ...prev,
                backside: value,
                sameBack: value === 'same',
                stringType: prev.applicationMethod === 'string' ? prev.stringType : 'black',
            };
        });

        if (value === 'same') {
            setDesign(prev => ({
                ...prev,
                backside: value,
                sameBack: true,
                textB: prev.textA,
                textPositionB: prev.textPositionA,
                imageB: prev.imageA,
                imagePositionB: prev.imagePositionA,
            }));
        }
    };

    const stringTypeColors = {
        black: {
            start: 'rgba(102, 102, 102, 0.8)',
            mid: 'rgba(68, 68, 68, 0.9)',
            end: 'rgba(68, 68, 68, 1)',
            shadowEnd: 'rgba(68, 68, 68, 1)',
        },
        white: {
            start: 'rgba(245, 245, 245, 0.8)',
            mid: 'rgba(224, 224, 224, 0.9)',
            end: 'rgba(224, 224, 224, 1)',
            shadowEnd: 'rgba(224, 224, 224, 1)',
        },
        organic: {
            start: 'rgba(139, 139, 90, 0.8)',
            mid: 'rgba(107, 107, 69, 0.9)',
            end: 'rgba(107, 107, 69, 1)',
            shadowEnd: 'rgba(107, 107, 69, 1)',
        },
    };

    const renderCardContent = (side) => {
        const {
            verticalPadding,
            horizontalPadding,
            verticalFrameOffset,
            horizontalFrameOffset,
        } = calculateFrameOffsets();

        const size = sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed');
        const scale = calculateScaleFactor();
        const containerClassName = `card-container ${design.frameStyle !== 'none' ? `with-${design.frameStyle}-frame` : ''}`;
        const textureClass = design.cardboardType === 'recycled' ? 'recycled-texture' : design.cardboardType === 'premium' ? 'premium-texture' : 'standard-texture';
        const isCircle = design.shape === 'circle';
        const isSquare = design.shape === 'square';
        const borderRadius = isCircle ? '50%' : (design.extras.cornerRadius ? '8px' : '0');
        const textPosition = side === 'A' ? textPositionA : textPositionB;
        const currentImage = side === 'A' ? imageA : imageB;
        const imagePosition = side === 'A' ? imagePositionA : imagePositionB;

        const getPlaceholderText = () => {
            if (design.backside === 'same' || design.backside === 'blank') {
                return 'Enter Text';
            }
            return side === 'A' ? 'Enter Text for side A' : 'Enter Text for side B';
        };

        const displayContent = side === 'A'
            ? design.textA
            : (design.backside === 'different' ? design.textB : design.textA);

        const hasText = !!design[`text${side}`];

        // Blank arka yüz için render
        if (side === 'B' && design.backside === 'blank') {
            return (
                <div className={containerClassName} style={{ borderRadius, position: 'relative' }}>
                    <div
                        className={`card-content-wrapper ${textureClass}`}
                        style={{
                            background: currentImage ? 'transparent' : design.backgroundColor,
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            borderRadius,
                            position: 'relative',
                            zIndex: 1,
                            overflow: 'hidden',
                        }}
                    />
                    {design.applicationMethod === 'hole' && (
                        <div
                            className="punch-hole"
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: `${HOLE_TOP_OFFSET_MM * scale}px`,
                                width: `${HOLE_DIAMETER_MM * scale}px`,
                                height: `${HOLE_DIAMETER_MM * scale}px`,
                                transform: 'translate(-50%, -50%)',
                                borderRadius: '50%',
                                background: '#fff',
                                border: '2px solid #ddd',
                                zIndex: 2,
                            }}
                        />
                    )}
                    {design.applicationMethod === 'string' && (
                        <div className="string-attachment">
                            <div className="string-shadow"></div>
                        </div>
                    )}
                </div>
            );
        }

        const iconEl = design.icon !== 'none' ? (
            <span
                className="icon"
                style={{
                    width: `${Math.round(design.fontSize * 0.8)}px`,
                    height: `${Math.round(design.fontSize * 0.8)}px`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    verticalAlign: 'middle',
                }}
            >
                <Icon name={design.icon} color={design.textColor} size={Math.round(design.fontSize * 0.8)} />
            </span>
        ) : null;

        const iconShouldShow = !!design[`text${side}`];

        return (
            <div className={containerClassName} style={{ borderRadius, position: 'relative' }}>
                <div
                    className={`card-content-wrapper ${design.frameStyle !== 'none' ? `${design.frameStyle}-frame` : ''} ${currentImage ? '' : textureClass}`}
                    style={{
                        background: currentImage ? 'transparent' : design.backgroundColor,
                        color: design.textColor,
                        fontFamily: design.font,
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        borderRadius,
                        position: 'relative',
                        zIndex: '1',
                        overflow: 'hidden',
                    }}
                >
                    {design.frameStyle === 'single' && (
                        <div
                            className="outer-frame"
                            style={{
                                borderColor: design.textColor,
                                position: 'absolute',
                                top: '8px',
                                left: '8px',
                                right: '8px',
                                bottom: '8px',
                                border: '2px solid',
                                borderRadius,
                                boxSizing: 'border-box',
                            }}
                        />
                    )}
                    {currentImage && (
                        <img
                            src={currentImage}
                            alt={`Hangtag Image ${side}`}
                            className="card-image"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                                transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imagePosition.scale}) rotate(${imagePosition.rotate}deg)`,
                                position: 'absolute',
                                zIndex: 0,
                            }}
                        />
                    )}
                    {design[`text${side}`] && (
                        <div
                            className="card-text-display"
                            style={{
                                fontSize: `${Math.round(design.fontSize)}px`,
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                padding: design.frameStyle === 'single'
                                    ? `${verticalFrameOffset + 8}px ${horizontalFrameOffset + 8}px`
                                    : `${verticalPadding}px ${horizontalPadding}px`,
                                margin: '0',
                                lineHeight: 1.2,
                                boxSizing: 'border-box',
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transform: `translate(${textPosition.x}px, ${textPosition.y}px) scale(${textPosition.scale}) rotate(${textPosition.rotate}deg)`,
                                transformOrigin: 'center',
                                zIndex: 1,
                            }}
                        >
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: `${Math.max(2, Math.round(design.fontSize * 0.2))}px`,
                                    textAlign: 'center',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    overflow: 'hidden',
                                    whiteSpace: 'pre-wrap', // Allow multiline text
                                    lineHeight: 1.2,
                                }}
                            >
                                {iconShouldShow && (design.iconPosition === 'left' || design.iconPosition === 'both') && iconEl}
                                <span
                                    className="card-content"
                                    style={{
                                        display: 'inline-block',
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        textAlign: 'center',
                                        whiteSpace: 'pre-wrap', // Allow multiline text
                                        lineHeight: 1.2,
                                        color: design.textColor,
                                    }}
                                >
                                    {displayContent}
                                </span>
                                {iconShouldShow && (design.iconPosition === 'right' || design.iconPosition === 'both') && iconEl}
                            </span>
                        </div>
                    )}
                </div>
                {design.applicationMethod === 'hole' && (
                    <div
                        className="punch-hole"
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: `${HOLE_TOP_OFFSET_MM * scale}px`,
                            width: `${HOLE_DIAMETER_MM * scale}px`,
                            height: `${HOLE_DIAMETER_MM * scale}px`,
                            transform: 'translate(-50%, -50%)',
                            borderRadius: '50%',
                            background: '#fff',
                            border: '2px solid #ddd',
                            zIndex: 2,
                        }}
                    />
                )}
                {design.applicationMethod === 'string' && (
                    <>
                        {/* Delik */}
                        <div
                            className="punch-hole"
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: `${HOLE_TOP_OFFSET_MM * scale}px`,
                                width: `${HOLE_DIAMETER_MM * scale}px`,
                                height: `${HOLE_DIAMETER_MM * scale}px`,
                                transform: 'translate(-50%, -50%)',
                                borderRadius: '50%',
                                background: '#fff',
                                border: '2px solid #ddd',
                                zIndex: 2,
                            }}
                        />
                        {/* İp efekti */}
                        <div
                            className="string-attachment"
                            style={{
                                background: `linear-gradient(to bottom, 
                                    ${(stringTypeColors[design.stringType] ? stringTypeColors[design.stringType].start : 'rgba(102, 102, 102, 0.8)')}, 
                                    ${(stringTypeColors[design.stringType] ? stringTypeColors[design.stringType].mid : 'rgba(68, 68, 68, 0.9)')} 40%, 
                                    ${(stringTypeColors[design.stringType] ? stringTypeColors[design.stringType].end : 'rgba(68, 68, 68, 1)')})`,
                            }}
                        >
                            <div className="string-shadow"></div>
                        </div>
                    </>
                )}
            </div>
        );
    };

    // İkon pozisyonu değişikliğini yöneten fonksiyon
    const handleIconPositionChange = (position) => {
        setDesign(prev => {
            // Eğer pozisyon 'none' ise, ikonu da 'none' yap
            if (position === 'none') {
                return { ...prev, iconPosition: position, icon: 'none' };
            }
            // Eğer ikon 'none' ise ve yeni pozisyon 'none' değilse, varsayılan bir ikon seç
            if (prev.icon === 'none' && position !== 'none') {
                return { ...prev, iconPosition: position, icon: 'star' };
            }
            // Diğer durumlarda sadece pozisyonu güncelle
            return { ...prev, iconPosition: position };
        });
    };

    // İkon seçimi değişikliğini yöneten fonksiyon
    const handleIconChange = (icon) => {
        setDesign(prev => {
            // Eğer ikon 'none' ise, pozisyonu da 'none' yap
            if (icon === 'none') {
                return { ...prev, icon, iconPosition: 'none' };
            }
            // Eğer pozisyon 'none' ise, pozisyonu 'left' yap
            if (prev.iconPosition === 'none') {
                return { ...prev, icon, iconPosition: 'left' };
            }
            // Diğer durumlarda sadece ikonu güncelle
            return { ...prev, icon };
        });
    };

    useEffect(() => {
        if (cardARef.current) {
            const wrapper = cardARef.current.querySelector('.card-content-wrapper');
            if (wrapper) {
                wrapper.style.background = design.backgroundColor;
            }
        }
        if (cardBRef.current) {
            const wrapper = cardBRef.current.querySelector('.card-content-wrapper');
            if (wrapper) {
                wrapper.style.background = design.backgroundColor;
            }
        }
    }, [design.backgroundColor]);

    const prices = useMemo(() => calculatePrice(), [calculatePrice]);

    const adjustTextPosition = (side, action) => {
        const step = 5;
        const scaleStep = 0.1;
        const setter = side === 'A' ? setTextPositionA : setTextPositionB;
        setter((prev) => {
            switch (action) {
                case 'zoomIn':
                    return { ...prev, scale: Math.min(prev.scale + scaleStep, 2.0) };
                case 'zoomOut':
                    return { ...prev, scale: Math.max(prev.scale - scaleStep, 0.5) };
                case 'up':
                    return { ...prev, y: prev.y - step };
                case 'down':
                    return { ...prev, y: prev.y + step };
                case 'left':
                    return { ...prev, x: prev.x - step };
                case 'right':
                    return { ...prev, x: prev.x + step };
                case 'rotate':
                    return { ...prev, rotate: (prev.rotate + 90) % 360 };
                case 'center':
                    return { x: 0, y: 0, scale: 1, rotate: 0 };
                default:
                    return prev;
            }
        });
    };

    const adjustImagePosition = (side, action) => {
        const step = 5;
        const scaleStep = 0.1;
        const setter = side === 'A' ? setImagePositionA : setImagePositionB;
        setter((prev) => {
            switch (action) {
                case 'zoomIn':
                    return { ...prev, scale: Math.min(prev.scale + scaleStep, 2.0) };
                case 'zoomOut':
                    return { ...prev, scale: Math.max(prev.scale - scaleStep, 0.5) };
                case 'up':
                    return { ...prev, y: prev.y - step };
                case 'down':
                    return { ...prev, y: prev.y + step };
                case 'left':
                    return { ...prev, x: prev.x - step };
                case 'right':
                    return { ...prev, x: prev.x + step };
                case 'rotate':
                    return { ...prev, rotate: (prev.rotate + 90) % 360 };
                case 'center':
                    return { x: 0, y: 0, scale: 1, rotate: 0 };
                default:
                    return prev;
            }
        });
    };

    const calculateContrastRatio = (color1, color2) => {
        const hexToRgb = (hex) => {
            const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
            hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result
                ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16),
                }
                : { r: 0, g: 0, b: 0 };
        };

        // Renklerin RGB değerlerini al
        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        // Renk farklılığını hesapla (Delta E)
        const calculateDeltaE = (rgb1, rgb2) => {
            // RGB'den LAB renk uzayına dönüşüm için yardımcı fonksiyonlar
            const rgbToXyz = (r, g, b) => {
                r = r / 255;
                g = g / 255;
                b = b / 255;

                r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
                g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
                b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

                r = r * 100;
                g = g * 100;
                b = b * 100;

                const x = r * 0.4124 + g * 0.3576 + b * 0.1805;
                const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
                const z = r * 0.0193 + g * 0.1192 + b * 0.9505;

                return [x, y, z];
            };

            const xyzToLab = (x, y, z) => {
                x = x / 95.047;
                y = y / 100;
                z = z / 108.883;

                x = x > 0.008856 ? Math.pow(x, 1 / 3) : (7.787 * x) + (16 / 116);
                y = y > 0.008856 ? Math.pow(y, 1 / 3) : (7.787 * y) + (16 / 116);
                z = z > 0.008856 ? Math.pow(z, 1 / 3) : (7.787 * z) + (16 / 116);

                const l = (116 * y) - 16;
                const a = 500 * (x - y);
                const b = 200 * (y - z);

                return [l, a, b];
            };

            // RGB'den LAB'a dönüşüm
            const [x1, y1, z1] = rgbToXyz(rgb1.r, rgb1.g, rgb1.b);
            const [x2, y2, z2] = rgbToXyz(rgb2.r, rgb2.g, rgb2.b);

            const [l1, a1, b1] = xyzToLab(x1, y1, z1);
            const [l2, a2, b2] = xyzToLab(x2, y2, z2);

            // Delta E hesaplama (CIE76 formülü)
            return Math.sqrt(
                Math.pow(l2 - l1, 2) +
                Math.pow(a2 - a1, 2) +
                Math.pow(b2 - b1, 2)
            );
        };

        // Renk farklılığını hesapla
        const deltaE = calculateDeltaE(rgb1, rgb2);

        // Renk parlaklık farkını hesapla
        const getBrightness = (rgb) => {
            return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        };

        const brightness1 = getBrightness(rgb1);
        const brightness2 = getBrightness(rgb2);
        const brightnessDiff = Math.abs(brightness1 - brightness2);

        // Sonuç objesi
        return {
            deltaE: deltaE,
            brightnessDiff: brightnessDiff,
            isSimilar: deltaE < 30, // Renklerin benzerliği için eşik değeri
            isLowContrast: brightnessDiff < 50, // Düşük kontrast için eşik değeri
            needsWarning: deltaE < 30 && brightnessDiff < 50 // Her iki koşul da sağlanıyorsa uyarı gerekir
        };
    };

    const checkColors = (newTextColor, newBgColor, setDesign, colorType) => {
        const hasText = (design.textA && design.textA.trim() !== '') || (design.textB && design.textB.trim() !== '');
        if (!hasText) {
            return true;
        }
        if (hasText && newTextColor === newBgColor) {
            setOverlay({
                show: true,
                message: t('text_color_and_background_color_cannot_be_the_same', 'Text color and background color cannot be the same!'),
                onConfirm: null,
            });
            return false;
        }

        const contrast = calculateContrastRatio(newTextColor, newBgColor);

        if (contrast.needsWarning) {
            setOverlay({
                show: true,
                message: t('warning_the_selected_colors_are_too_similar_and_may_be_hard_to_read_do_you_want_to_proceed_anyway', `Warning: The selected colors are too similar and may be hard to read.
                     Do you want to proceed anyway?`),
                onConfirm: () => setDesign(prev => ({
                    ...prev,
                    [colorType]: colorType === 'textColor' ? newTextColor : newBgColor
                })),
            });
            return false;
        }

        return true;
    };

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 1200);
            if (window.innerWidth > 1200) setIsMobilePreviewOpen(false);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const hasTextA = design.textA && design.textA.trim() !== '';
    const hasImageA = imageA !== null;
    const hasContentA = hasTextA || hasImageA;
    const hasTextB = design.textB && design.textB.trim() !== '';
    const hasImageB = imageB !== null;
    const hasContentB = hasTextB || hasImageB;
    const isDesignValid = hasContentA && (design.backside !== 'different' || hasContentB);

    return (
        <div className="card-designer-wrapper" id="ht-hangtag-designer-root">
            <div className="orientation-warning-overlay">
                <span>{t('please_rotate_your_device_to_portrait', 'Please rotate your device to portrait')}</span>
            </div>
            <div className={`designer-container ${design.backside !== 'blank' ? 'has-side-switcher' : ''}`} style={{ '--canvas-height': `${canvasHeight}px` }}>
                {/* Mobile Only Side Switcher Container - always rendered to prevent layout shift */}
                <div className="mobile-only-side-switcher-container" style={design.backside === 'blank' ? { visibility: 'hidden', pointerEvents: 'none', height: '0', padding: '0', overflow: 'hidden', minHeight: '0', borderBottom: 'none' } : {}}>
                    <button
                        type="button"
                        className={`switcher-btn ${activeMobileSide === 'A' ? 'active' : ''}`}
                        onClick={() => setActiveMobileSide('A')}
                    >
                        {t('Side A', 'Side A')}
                    </button>
                    <button
                        type="button"
                        className={`switcher-btn ${activeMobileSide === 'B' ? 'active' : ''}`}
                        onClick={() => setActiveMobileSide('B')}
                    >
                        {t('Side B', 'Side B')}
                    </button>
                </div>

                {/* Mobil Kategori Sekmeleri */}
                <div className="mobile-category-tabs">
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'shape_size' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('shape_size'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Shape & Size', 'Shape & Size')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'material_color' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('material_color'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Material & Color', 'Material & Color')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'design_text' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('design_text'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Design & Text', 'Design & Text')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'accessories' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('accessories'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Accessories', 'Accessories')}
                    </button>
                </div>

                <div className="designer-controls" data-active-mobile-tab={activeMobileTab}>

                    {/* Şekil Seçimi */}
                    <div className="control-group tab-shape_size">
                        <div className="group-title">
                            {t('Shape', 'Shape')}
                            <span className="help-icon">
                                ?
                                <span className="tooltip">{t('select_the_shape_of_your_hangtag', 'Select the shape of your hangtag.')}</span>
                            </span>
                        </div>
                        <div className="shape-options">
                            {shapeOptions.map((shape) => (
                                <div
                                    key={shape.value}
                                    className={`shape-option ${design.shape === shape.value ? 'selected' : ''}`}
                                    onClick={() => handleShapeChange(shape.value)}
                                >
                                    <div className="shape-preview">
                                        <div className={`shape-box ${shape.value}`} />
                                    </div>
                                    <div className="shape-details">
                                        <div className="shape-name">{t(shape.name, shape.name)}</div>
                                        <div className="shape-dimensions">{t(shape.dimensions, shape.dimensions)}</div>
                                        <div className="shape-info">{t(shape.info, shape.info)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Boyut Seçimi */}
                    {design.shape === 'rectangle' && (
                        <div className="control-group tab-shape_size">
                            <div className="group-title">
                                {t('Size', 'Size')}
                                <span className="help-icon">
                                    ?
                                    <span className="tooltip">{t('select_the_size_of_your_hangtag', 'Select the size of your hangtag.')}</span>
                                </span>
                            </div>
                            <div className="size-options">
                                {sizeOptions
                                    .filter((size) => size.name !== 'Fixed')
                                    .map((size) => (
                                        <div
                                            key={size.name}
                                            className={`size-option ${design.size === size.name.toLowerCase() ? 'selected' : ''}`}
                                            onClick={() => setDesign((prev) => ({
                                                ...prev,
                                                size: size.name.toLowerCase(),
                                            }))}
                                            data-size={size.name.toLowerCase()}
                                        >
                                            <div className="size-preview">
                                                <div className="size-box" />
                                            </div>
                                            <div className="size-details">
                                                <div className="size-name">{t(size.name, size.name)}</div>
                                                <div className="size-dimensions">{`${size.width}x${size.height}mm`}</div>
                                                <div className="size-info">{t(size.info, size.info)}</div>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}



                    {/* Arka Yüz Seçenekleri */}
                    <div className="control-group tab-shape_size">
                        <div className="group-title">
                            {t('Back Side', 'Back Side')}
                            <span className="help-icon">
                                ?
                                <span className="tooltip">{t('choose_whether_the_back_side_is_the_same_as_the_front_or_different', 'Choose whether the back side is the same as the front or different.')}</span>
                            </span>
                        </div>
                        <div className="backside-options">
                            {backsideOptions.map((option) => (
                                <div
                                    key={option.value}
                                    className={`backside-option ${design.backside === option.value ? 'selected' : ''}`}
                                    onClick={() => handleBacksideChange(option.value)}
                                >
                                    <div className="backside-preview">
                                        <div className="backside-icon">
                                            {option.icon}
                                        </div>
                                    </div>
                                    <div className="backside-details">
                                        <div className="backside-name">{t(option.name, option.name)}</div>
                                        <div className="backside-info">{t(option.info, option.info)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="control-group tab-material_color">
                        <div className="group-title">
                            {t('Material', 'Material')}
                            <span className="help-icon">?<span className="tooltip">{t('choose_the_material_for_your_hangtag', 'Choose the material for your hangtag.')}</span></span>
                        </div>
                        <div className="material-options">
                            {[
                                {
                                    name: 'standard',
                                    label: 'Standard',
                                    descKey: 'basic_cardboard_material',
                                    defaultDesc: 'Basic cardboard material',
                                    gsm: '300-350 gsm',
                                    icon: '📄'
                                },
                                {
                                    name: 'premium',
                                    label: 'Premium',
                                    descKey: 'high_quality_cardboard',
                                    defaultDesc: 'High-quality cardboard',
                                    gsm: '350-400 gsm',
                                    icon: '✨'
                                },
                                {
                                    name: 'recycled',
                                    label: 'Recycled',
                                    descKey: '100_recycled_material',
                                    defaultDesc: '100% recycled material',
                                    gsm: '320-400 gsm',
                                    icon: '♻️'
                                }
                            ].map((material) => (
                                <button
                                    key={material.name}
                                    className={`material-button ${design.cardboardType === material.name ? 'selected' : ''}`}
                                    onClick={() => {
                                        // Önce önceki materyal recycled mı kontrol et
                                        const wasRecycled = design.cardboardType === 'recycled';

                                        setDesign(prev => ({
                                            ...prev,
                                            cardboardType: material.name,
                                            // Eğer yeni materyal recycled ise #E5D3B3'e ayarla
                                            // Eğer önceki materyal recycled idi ve yeni materyal recycled değilse #FFFFFF'e ayarla
                                            // Diğer durumlarda mevcut backgroundColor'ı koru
                                            backgroundColor: material.name === 'recycled'
                                                ? '#E5D3B3'
                                                : (wasRecycled ? '#FFFFFF' : prev.backgroundColor)
                                        }));
                                    }}
                                >
                                    <div className="material-icon">{material.icon}</div>
                                    <div className="material-info">
                                        <div className="material-title">{t(material.label, material.label)}</div>
                                        <div className="material-desc">
                                            {t(material.descKey, material.defaultDesc)}
                                            <br />
                                            {material.gsm}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Metin Girişi (Side A) */}
                    {/* Two-Sided Text Controls */}
                    {!isMobile && (
                        <div className="control-group tab-design_text">
                            <div className="group-title">
                                {t('Hangtag Text', 'Hangtag Text')}
                                <span className="help-icon">
                                    ?
                                    <span className="tooltip">{t('enter_text_for_each_side_and_adjust_its_position', 'Enter text for each side and adjust its position.')}</span>
                                </span>
                            </div>

                            <div className="htd-two-sided-section">
                                <div className={`htd-sides-grid ${design.backside !== 'different' ? 'single' : ''}`}>
                                    {/* Side A Text */}
                                    <div className="htd-side-col">
                                        <div className="htd-side-title">{t('Side A', 'Side A')}</div>

                                        {(!textEditorOpenA && !design.textA) ? (
                                            <button
                                                className="htd-text-launch-btn"
                                                onClick={() => setTextEditorOpenA(true)}
                                            >
                                                {t('Enter text for Side A', 'Enter text for Side A')}
                                            </button>
                                        ) : (
                                            <div className="htd-text-panel">
                                                <div className="htd-text-panel-header">
                                                    <span>{t('Text - Side A', 'Text - Side A')}</span>
                                                    <button
                                                        className="htd-close-btn"
                                                        aria-label={t('Clear text', 'Clear text')}
                                                        onClick={() => {
                                                            setDesign(prev => ({ ...prev, textA: '' }));
                                                            setTextEditorOpenA(false);
                                                            setTextPositionA({ x: 0, y: 0, scale: 1, rotate: 0 });
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </div>

                                                <div className="text-input-container">
                                                    <textarea
                                                        value={design.textA}
                                                        onChange={(e) => setDesign((prev) => ({ ...prev, textA: e.target.value }))}
                                                        className="card-text-input"
                                                        placeholder=""
                                                        maxLength={200}
                                                        rows={4}
                                                    />
                                                </div>

                                                <div className="position-control-panel">
                                                    <div className="control-title">{t('Text Position & Size', 'Text Position & Size')}</div>
                                                    <div className="controls-grid">
                                                        <button className="control-button" onClick={() => adjustTextPosition('A', 'zoomIn')}>+</button>
                                                        <button className="control-button" onClick={() => adjustTextPosition('A', 'up')}>↑</button>
                                                        <button className="control-button" onClick={() => adjustTextPosition('A', 'zoomOut')}>−</button>
                                                        <button className="control-button" onClick={() => adjustTextPosition('A', 'left')}>←</button>
                                                        <button className="control-button center" onClick={() => adjustTextPosition('A', 'center')}>⌖</button>
                                                        <button className="control-button" onClick={() => adjustTextPosition('A', 'right')}>→</button>
                                                        <button className="control-button rotate" onClick={() => adjustTextPosition('A', 'rotate')}>⟳</button>
                                                        <button className="control-button" onClick={() => adjustTextPosition('A', 'down')}>↓</button>
                                                    </div>
                                                    <div className="scale-controls">
                                                        <span className="scale-value">{Math.round(textPositionA.scale * 100)}%</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Side B Text */}
                                    {design.backside === 'different' && (
                                        <div className="htd-side-col">
                                            <div className="htd-side-title">{t('Side B', 'Side B')}</div>

                                            {(!textEditorOpenB && !design.textB) ? (
                                                <button
                                                    className="htd-text-launch-btn"
                                                    onClick={() => setTextEditorOpenB(true)}
                                                >
                                                    {t('Enter text for Side B', 'Enter text for Side B')}
                                                </button>
                                            ) : (
                                                <div className="htd-text-panel">
                                                    <div className="htd-text-panel-header">
                                                        <span>{t('Text - Side B', 'Text - Side B')}</span>
                                                        <button
                                                            className="htd-close-btn"
                                                            aria-label={t('Clear text', 'Clear text')}
                                                            onClick={() => {
                                                                setDesign(prev => ({ ...prev, textB: '' }));
                                                                setTextEditorOpenB(false);
                                                                setTextPositionB({ x: 0, y: 0, scale: 1, rotate: 0 });
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>

                                                    <div className="text-input-container">
                                                        <textarea
                                                            value={design.textB}
                                                            onChange={(e) => setDesign((prev) => ({ ...prev, textB: e.target.value }))}
                                                            className="card-text-input"
                                                            placeholder=""
                                                            maxLength={200}
                                                            rows={4}
                                                        />
                                                    </div>

                                                    <div className="position-control-panel">
                                                        <div className="control-title">{t('Text Position & Size', 'Text Position & Size')}</div>
                                                        <div className="controls-grid">
                                                            <button className="control-button" onClick={() => adjustTextPosition('B', 'zoomIn')}>+</button>
                                                            <button className="control-button" onClick={() => adjustTextPosition('B', 'up')}>↑</button>
                                                            <button className="control-button" onClick={() => adjustTextPosition('B', 'zoomOut')}>−</button>
                                                            <button className="control-button" onClick={() => adjustTextPosition('B', 'left')}>←</button>
                                                            <button className="control-button center" onClick={() => adjustTextPosition('B', 'center')}>⌖</button>
                                                            <button className="control-button" onClick={() => adjustTextPosition('B', 'right')}>→</button>
                                                            <button className="control-button rotate" onClick={() => adjustTextPosition('B', 'rotate')}>⟳</button>
                                                            <button className="control-button" onClick={() => adjustTextPosition('B', 'down')}>↓</button>
                                                        </div>
                                                        <div className="scale-controls">
                                                            <span className="scale-value">{Math.round(textPositionB.scale * 100)}%</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Metin Girişi (Side B) - Same Back Seçili Değilse */}
                    {/* Two-Sided Image Upload Controls */}
                    <div className="control-group tab-design_text">
                        <div className="group-title">
                            {t('Images', 'Images')}
                            <span className="help-icon">
                                ?
                                <span className="tooltip">{t('upload_images_for_each_side_click_the_x_to_remove_or_click_the_box_to_replace', 'Upload images for each side. Click the X to remove or click the box to replace.')}</span>
                            </span>
                        </div>

                        <div className="htd-two-sided-section">
                            <div className={`htd-sides-grid ${design.backside !== 'different' ? 'single' : ''}`}>
                                {/* Side A Image */}
                                <div className="htd-side-col">
                                    <div className="htd-side-title">{t('Side A', 'Side A')}</div>

                                    <input
                                        id="htd-upload-a"
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleImageUpload('A', e)}
                                        className="htd-upload-input"
                                    />

                                    <label htmlFor="htd-upload-a" className="htd-upload-box">
                                        {imageA ? (
                                            <>
                                                <img src={imageA} alt="Upload preview A" className="htd-upload-preview" />
                                                <button
                                                    className="htd-remove-btn"
                                                    aria-label={t('Remove image', 'Remove image')}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setImageA(null);
                                                        setRawImageAId(0);
                                                        setImagePositionA({ x: 0, y: 0, scale: 1, rotate: 0 });
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </>
                                        ) : (
                                            <div className="htd-upload-content">
                                                <svg className="htd-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                    <polyline points="17 8 12 3 7 8" />
                                                    <line x1="12" y1="3" x2="12" y2="15" />
                                                </svg>
                                                <span className="htd-upload-text">{t('Click to upload an image', 'Click to upload an image')}</span>
                                                <span className="htd-upload-hint">{t('Support for JPG, PNG files', 'Support for JPG, PNG files')}</span>
                                            </div>
                                        )}
                                    </label>

                                    {imageA && (
                                        <div className="position-control-panel" style={{ marginTop: '1rem' }}>
                                            <div className="control-title">{t('Image Position & Size', 'Image Position & Size')}</div>
                                            <div className="controls-grid">
                                                <button className="control-button" onClick={() => adjustImagePosition('A', 'zoomIn')}>+</button>
                                                <button className="control-button" onClick={() => adjustImagePosition('A', 'up')}>↑</button>
                                                <button className="control-button" onClick={() => adjustImagePosition('A', 'zoomOut')}>−</button>
                                                <button className="control-button" onClick={() => adjustImagePosition('A', 'left')}>←</button>
                                                <button className="control-button center" onClick={() => adjustImagePosition('A', 'center')}>⌖</button>
                                                <button className="control-button" onClick={() => adjustImagePosition('A', 'right')}>→</button>
                                                <button className="control-button rotate" onClick={() => adjustImagePosition('A', 'rotate')}>⟳</button>
                                                <button className="control-button" onClick={() => adjustImagePosition('A', 'down')}>↓</button>
                                            </div>
                                            <div className="scale-controls">
                                                <span className="scale-value">{Math.round(imagePositionA.scale * 100)}%</span>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Side B Image */}
                                {design.backside === 'different' && (
                                    <div className="htd-side-col">
                                        <div className="htd-side-title">{t('Side B', 'Side B')}</div>

                                        <input
                                            id="htd-upload-b"
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload('B', e)}
                                            className="htd-upload-input"
                                        />

                                        <label htmlFor="htd-upload-b" className="htd-upload-box">
                                            {imageB ? (
                                                <>
                                                    <img src={imageB} alt="Upload preview B" className="htd-upload-preview" />
                                                    <button
                                                        className="htd-remove-btn"
                                                        aria-label={t('Remove image', 'Remove image')}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setImageB(null);
                                                            setRawImageBId(0);
                                                            setImagePositionB({ x: 0, y: 0, scale: 1, rotate: 0 });
                                                        }}
                                                    >
                                                        ×
                                                    </button>
                                                </>
                                            ) : (
                                                <div className="htd-upload-content">
                                                    <svg className="htd-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                        <polyline points="17 8 12 3 7 8" />
                                                        <line x1="12" y1="3" x2="12" y2="15" />
                                                    </svg>
                                                    <span className="htd-upload-text">{t('Click to upload an image', 'Click to upload an image')}</span>
                                                    <span className="htd-upload-hint">{t('Support for JPG, PNG files', 'Support for JPG, PNG files')}</span>
                                                </div>
                                            )}
                                        </label>

                                        {imageB && (
                                            <div className="position-control-panel" style={{ marginTop: '1rem' }}>
                                                <div className="control-title">{t('Image Position & Size', 'Image Position & Size')}</div>
                                                <div className="controls-grid">
                                                    <button className="control-button" onClick={() => adjustImagePosition('B', 'zoomIn')}>+</button>
                                                    <button className="control-button" onClick={() => adjustImagePosition('B', 'up')}>↑</button>
                                                    <button className="control-button" onClick={() => adjustImagePosition('B', 'zoomOut')}>−</button>
                                                    <button className="control-button" onClick={() => adjustImagePosition('B', 'left')}>←</button>
                                                    <button className="control-button center" onClick={() => adjustImagePosition('B', 'center')}>⌖</button>
                                                    <button className="control-button" onClick={() => adjustImagePosition('B', 'right')}>→</button>
                                                    <button className="control-button rotate" onClick={() => adjustImagePosition('B', 'rotate')}>⟳</button>
                                                    <button className="control-button" onClick={() => adjustImagePosition('B', 'down')}>↓</button>
                                                </div>
                                                <div className="scale-controls">
                                                    <span className="scale-value">{Math.round(imagePositionB.scale * 100)}%</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* Font Kategorisi ve Seçimi */}
                    {!isMobile && hasAnyText && (
                        <div className="control-group tab-design_text">
                            <div className="group-title">
                                {t('Font Style', 'Font Style')}
                                <span className="help-icon">
                                    ?
                                    <span className="tooltip">{t('choose_from_a_variety_of_fonts_to_customize_your_hangtag_s_appearance', "Choose from a variety of fonts to customize your hangtag's appearance.")}</span>
                                </span>
                            </div>
                            <div className="font-category-selector">
                                <button
                                    className="scroll-button left"
                                    onClick={() => scrollFontCategories('left')}
                                    disabled={fontScrollPosition <= 0}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M15 18l-6-6 6-6" />
                                    </svg>
                                </button>
                                <div className="font-category-list" ref={fontCategorySliderRef}>
                                    {Object.entries(fontCategories).map(([category, fonts]) => (
                                        <button
                                            key={category}
                                            className={`font-category-button ${fontCategory === category ? 'active' : ''}`}
                                            onClick={() => setFontCategory(category)}
                                        >
                                            <span className="font-preview-text" style={{ fontFamily: fonts[0] }}>
                                                Aa
                                            </span>
                                            <span className="font-category-name">{t(category, category)}</span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    className="scroll-button right"
                                    onClick={() => scrollFontCategories('right')}
                                    disabled={
                                        fontScrollPosition >=
                                        ((fontCategorySliderRef.current ? fontCategorySliderRef.current.scrollWidth : 0) - (fontCategorySliderRef.current ? fontCategorySliderRef.current.clientWidth : 0))
                                    }
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>
                            </div>
                            <div className="font-options-grid">
                                {fontCategories[fontCategory].map((fontName) => (
                                    <button
                                        key={fontName}
                                        className={`font-option ${design.font === fontName ? 'selected' : ''}`}
                                        style={{ fontFamily: fontName }}
                                        onClick={() => setDesign((prev) => ({ ...prev, font: fontName }))}
                                    >
                                        <span className="font-preview">Aa</span>
                                        <span className="font-name">{fontName}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Renk Seçimi */}
                    <div className="control-group tab-material_color">
                        <div className="group-title">
                            {t('Colors', 'Colors')}
                            <span className="help-icon">?<span className="tooltip">{t('select_text_and_background_colors_for_your_hangtag', 'Select text and background colors for your hangtag.')}</span></span>
                        </div>
                        <div className="color-controls">
                            {!isMobile && hasAnyText && (
                                <div className="color-control">
                                    <label>{t('Text Color', 'Text Color')}</label>
                                    <div className="color-palette">
                                        {colorPalette.map((color) => (
                                            <button
                                                key={color}
                                                className={`color-swatch ${design.textColor === color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => {
                                                    if (checkColors(color, design.backgroundColor, setDesign, 'textColor')) {
                                                        setDesign(prev => ({ ...prev, textColor: color }));
                                                    }
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {design.cardboardType !== 'recycled' && (
                                <div className="color-control">
                                    <label>{t('Background Color', 'Background Color')}</label>
                                    <div className={`color-palette ${design.cardboardType === 'recycled' ? 'disabled' : ''}`}>
                                        {colorPalette.map((color) => (
                                            <button
                                                key={color}
                                                className={`color-swatch ${design.backgroundColor === color ? 'selected' : ''}`}
                                                style={{ backgroundColor: color }}
                                                onClick={() => {
                                                    if (design.cardboardType === 'recycled') return;
                                                    if (checkColors(design.textColor, color, setDesign, 'backgroundColor')) {
                                                        const isBlackBg = color.toLowerCase() === '#000000';
                                                        const targetTextColor = isBlackBg ? '#ffffff' : '#000000';
                                                        setDesign(prev => ({
                                                            ...prev,
                                                            backgroundColor: color,
                                                            textColor: targetTextColor
                                                        }));
                                                    }
                                                }}
                                                disabled={design.cardboardType === 'recycled'}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Uygulama Yöntemi */}
                    <div className="control-group tab-accessories">
                        <div className="group-title">
                            {t('Application Method', 'Application Method')}
                            <span className="help-icon">
                                ?
                                <span className="tooltip">{t('choose_how_the_hangtag_will_be_attached', 'Choose how the hangtag will be attached.')}</span>
                            </span>
                        </div>
                        <div className="application-options">
                            {applicationMethods.map((method) => (
                                <button
                                    key={method.name}
                                    className={`application-button ${design.applicationMethod === method.name ? 'selected' : ''}`}
                                    onClick={() => setDesign((prev) => ({ ...prev, applicationMethod: method.name, stringType: 'black' }))}
                                >
                                    <div className="method-icon">{method.icon}</div>
                                    <div className="application-info">
                                        <div className="application-title">{t(method.label, method.label)}</div>
                                        <div className="application-desc">{t(method.description, method.description)}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {design.applicationMethod === 'string' && (
                        <div className="control-group tab-accessories">
                            <div className="group-title">
                                {t('String Type', 'String Type')}
                                <span className="help-icon">
                                    ?
                                    <span className="tooltip">{t('choose_the_type_of_string_with_clip_for_attachment', 'Choose the type of string with clip for attachment.')}</span>
                                </span>
                            </div>
                            <div className="string-type-options">
                                {stringTypeOptions.map((type) => (
                                    <button
                                        key={type.name}
                                        className={`string-type-button ${design.stringType === type.name ? 'selected' : ''}`}
                                        onClick={() => setDesign((prev) => ({ ...prev, stringType: type.name }))}
                                        disabled={design.applicationMethod !== 'string'}
                                    >
                                        <div className="string-type-icon">{type.icon}</div>
                                        <div className="string-type-info">
                                            <div className="string-type-title">{t(type.label, type.label)}</div>
                                            <div className="string-type-desc">{t(type.description, type.description)}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Ekstra Özellikler */}
                    <div className="control-group tab-accessories">
                        <div className="group-title">
                            {t('Extras', 'Extras')}
                            <span className="help-icon">?<span className="tooltip">{t('add_optional_features_to_your_hangtag', 'Add optional features to your hangtag.')}</span></span>
                        </div>
                        <div className="extras-grid">
                            {[
                                {
                                    name: 'cornerRadius',
                                    label: 'Rounded Corners',
                                    description: 'Add rounded corners',
                                    icon: '⟲'
                                },
                                {
                                    name: 'glossyFinish',
                                    label: 'Glossy Finish',
                                    description: 'Add a glossy finish',
                                    icon: '✨'
                                },
                                {
                                    name: 'reinforced',
                                    label: 'Plastering',
                                    description: 'Double the thickness',
                                    icon: '🛡️'
                                }
                            ].map((extra) => (
                                <button
                                    key={extra.name}
                                    className={`extra-button ${design.extras[extra.name] ? 'selected' : ''}`}
                                    onClick={() => setDesign(prev => ({
                                        ...prev,
                                        extras: { ...prev.extras, [extra.name]: !prev.extras[extra.name] }
                                    }))}
                                    disabled={extra.name === 'cornerRadius' && (design.shape === 'circle')}
                                >
                                    <div className="extra-icon">{extra.icon}</div>
                                    <div className="extra-info">
                                        <div className="extra-title">{t(extra.label, extra.label)}</div>
                                        <div className="extra-desc">{t(extra.description, extra.description)}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Çerçeve Stili */}
                    <div className="control-group tab-material_color">
                        <div className="group-title">
                            {t('Frame Style', 'Frame Style')}
                            <span className="help-icon">
                                ?
                                <span className="tooltip">{t('choose_a_frame_style_for_your_hangtag', 'Choose a frame style for your hangtag.')}</span>
                            </span>
                        </div>
                        <div className="frame-style-buttons">
                            <button
                                className={`frame-button ${design.frameStyle === 'none' ? 'selected' : ''}`}
                                onClick={() => setDesign((prev) => ({ ...prev, frameStyle: 'none' }))}
                            >
                                <div className="frame-preview none-frame">
                                    <div className="size-preview frame-size-preview">
                                        <div className="size-box frame-size-box" />
                                    </div>
                                </div>
                                <span>{t('No Frame', 'No Frame')}</span>
                            </button>
                            <button
                                className={`frame-button ${design.frameStyle === 'single' ? 'selected' : ''}`}
                                onClick={() => setDesign((prev) => ({ ...prev, frameStyle: 'single' }))}
                            >
                                <div className="frame-preview single-frame">
                                    <div className="size-preview frame-size-preview">
                                        <div className="frame-size-box">
                                            <div className="size-box-frame" />
                                        </div>
                                    </div>
                                </div>
                                <span>{t('Single Frame', 'Single Frame')}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Kanvas Alanı */}
                <div className="designer-canvas" ref={canvasRef}>
                    <div className="preview-header">
                        <div className="preview-info">
                            <div className="preview-details">
                                <span>{t((design.shape || 'shape').charAt(0).toUpperCase() + (design.shape || 'shape').slice(1), (design.shape || 'shape').charAt(0).toUpperCase() + (design.shape || 'shape').slice(1))}</span>
                                <span>
                                    {t(design.cardboardType.charAt(0).toUpperCase() + design.cardboardType.slice(1), design.cardboardType.charAt(0).toUpperCase() + design.cardboardType.slice(1))}
                                </span>
                            </div>
                            {design.backside !== 'blank' && (
                                <div className="mobile-side-switcher">
                                    <button
                                        type="button"
                                        className={`switcher-tab ${activeMobileSide === 'A' ? 'active' : ''}`}
                                        onClick={() => setActiveMobileSide('A')}
                                    >
                                        {t('Side A', 'Side A')}
                                    </button>
                                    <button
                                        type="button"
                                        className={`switcher-tab ${activeMobileSide === 'B' ? 'active' : ''}`}
                                        onClick={() => setActiveMobileSide('B')}
                                    >
                                        {t('Side B', 'Side B')}
                                    </button>
                                </div>
                            )}
                            <span className="dimension-value">
                                {(() => {
                                    if (design.shape === 'rectangle') {
                                        const sizeOpt = sizeOptions.find((s) => s.name.toLowerCase() === design.size);
                                        return sizeOpt ? `${sizeOpt.width}x${sizeOpt.height}mm` : '';
                                    }
                                    return design.shape === 'square'
                                        ? t('50x50mm', '50x50mm')
                                        : t('50mm diameter', '50mm diameter');
                                })()}
                            </span>
                        </div>
                    </div>
                    <div className="canvas-frame" onClick={() => setZoomSide('A')} style={{ cursor: 'zoom-in' }}>
                        <div className="canvas-inner" style={{ display: 'flex', flexDirection: 'row', gap: design.shape === 'rectangle' && design.size === 'large' ? '48px' : '24px', justifyContent: 'center', alignItems: 'flex-start' }}>
                            {/* Side A Preview */}
                            <div className={`preview-side-wrapper side-a-wrapper ${activeMobileSide === 'A' ? 'mobile-active' : 'mobile-hidden'}`} onClick={(e) => { e.stopPropagation(); setZoomSide('A'); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'zoom-in' }}>
                                <div className="side-label" style={{ marginBottom: 16, fontWeight: 600, fontSize: 16, minHeight: 24 }}>{t('Side A', 'Side A')}</div>
                                <div
                                    id="preview-side-a"
                                    className="card-preview"
                                    ref={cardARef}
                                    style={{
                                        ...cardPreviewStyle(
                                            sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed'),
                                            calculateScaleFactor(),
                                            design.shape
                                        ),
                                        marginTop: 8
                                    }}
                                >
                                    {renderCardContent('A')}
                                </div>
                            </div>
                            {/* Side B Preview (blank hariç her zaman göster) */}
                            {design.backside !== 'blank' && (
                                <div className={`preview-side-wrapper side-b-wrapper ${activeMobileSide === 'B' ? 'mobile-active' : 'mobile-hidden'}`} onClick={(e) => { e.stopPropagation(); setZoomSide('B'); }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'zoom-in' }}>
                                    <div className="side-label" style={{ marginBottom: 16, fontWeight: 600, fontSize: 16, minHeight: 24 }}>{t('Side B', 'Side B')}</div>
                                    <div
                                        id="preview-side-b"
                                        className="card-preview"
                                        ref={cardBRef}
                                        style={{
                                            ...cardPreviewStyle(
                                                sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed'),
                                                calculateScaleFactor(),
                                                design.shape
                                            ),
                                            marginTop: 8
                                        }}
                                    >
                                        {design.backside === 'different' ? renderCardContent('B') : renderCardContent('A')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Price and Quantity - DESKTOP ONLY */}
                    <div className="order-section desktop-order-section">
                        <div className="order-quantity-row">
                            <label className="order-label" htmlFor="quantity">{t('Quantity', 'Quantity')}</label>
                            <select
                                id="quantity"
                                className="order-quantity"
                                value={design.quantity}
                                onChange={e => setDesign(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
                            >
                                {activeQuantities.map((value, i) => (
                                    <option key={i} value={value}>{value} {t('pieces', 'pieces')}</option>
                                ))}
                            </select>
                        </div>
                        {isDesignValid && (
                            <>
                                <div className="order-total-row">
                                    <span className="order-total-label">{t('Total Price:', 'Total Price:')}</span>
                                    <span className="price-with-discount">
                                        <span className="order-total-value">{getCurrencySymbol()}{Number(prices.total).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        {prices.discountRate > 0 && !prices.isMarkup && (
                                            <span className="discount-info">
                                                -{prices.discountRate}% {t('off', 'off')}
                                            </span>
                                        )}
                                    </span>
                                </div>
                                <div className="order-per-piece">{getCurrencySymbol()} {Number(prices.perPiece).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('per piece', 'per piece')}</div>
                            </>
                        )}
                        <button className="order-add-to-cart" onClick={addToCart} disabled={loading || uploadingA || uploadingB || !isDesignValid}>
                            {loading ? t('adding_to_cart', 'Adding to cart...') : (uploadingA || uploadingB) ? t('uploading_logo', 'Uploading image...') : t('add_to_cart', 'Add to Cart')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Order Section - Direct child of designer-container for correct position:fixed */}
            <div className="order-section mobile-order-section">
                <div className="quantity-selector">
                    <label htmlFor="quantity-mobile">{t('Quantity', 'Quantity')}</label>
                    <select
                        id="quantity-mobile"
                        value={design.quantity}
                        onChange={e => setDesign(prev => ({ ...prev, quantity: parseInt(e.target.value) }))}
                    >
                        {activeQuantities.map((value, i) => (
                            <option key={i} value={value}>{value} {t('pieces', 'pieces')}</option>
                        ))}
                    </select>
                </div>
                {isDesignValid && prices.discountRate > 0 && !prices.isMarkup && (
                    <span className="discount-badge-stuck">
                        -{prices.discountRate}%
                    </span>
                )}
                {isDesignValid && (
                    <div className="price-info">
                        <div className="total-price">
                            <span>{getCurrencySymbol()}{Number(prices.total).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                        <div className="price-per-piece">
                            {getCurrencySymbol()}{Number(prices.perPiece).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('per piece', 'per piece')}
                        </div>
                    </div>
                )}
                <button className="add-to-cart-button" onClick={addToCart} disabled={loading || uploadingA || uploadingB || !isDesignValid}>
                    {loading ? t('adding_to_cart', 'Adding to cart...') : (uploadingA || uploadingB) ? t('uploading_logo', 'Uploading image...') : t('add_to_cart', 'Add to Cart')}
                </button>
            </div>

            {/* Overlay for warnings (e.g., low contrast) */}
            {overlay.show && (
                <div className="overlay">
                    <div className="overlay-content">
                        <p>{overlay.message}</p>
                        <div className="overlay-buttons">
                            {overlay.onConfirm && (
                                <button
                                    className="confirm-button"
                                    onClick={() => {
                                        overlay.onConfirm();
                                        setOverlay({ show: false, message: '', onConfirm: null });
                                    }}
                                >
                                    {t('Proceed', 'Proceed')}
                                </button>
                            )}
                            <button
                                className="cancel-button"
                                onClick={() => setOverlay({ show: false, message: '', onConfirm: null })}
                            >
                                {overlay.onConfirm ? t('Cancel', 'Cancel') : t('OK', 'OK')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Click-to-Zoom Lightbox Modal */}
            {zoomSide && createPortal(
                <div
                    className="preview-lightbox-overlay"
                    onClick={() => setZoomSide(null)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(15, 15, 15, 0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 999999,
                        cursor: 'zoom-out'
                    }}
                >
                    <div
                        className="lightbox-preview-container"
                        onClick={() => setZoomSide(null)}
                        style={{
                            transformOrigin: 'center center',
                            display: 'flex',
                            gap: '24px',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {zoomSide === 'A' && (
                            <div
                                className="card-preview lightbox-card-zoom"
                                style={cardPreviewStyle(
                                    sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed'),
                                    calculateScaleFactor() * 1.3,
                                    design.shape,
                                    true
                                )}
                            >
                                {renderCardContent('A')}
                            </div>
                        )}
                        {zoomSide === 'B' && design.backside !== 'blank' && (
                            <div
                                className="card-preview lightbox-card-zoom"
                                style={cardPreviewStyle(
                                    sizeOptions.find((s) => s.name.toLowerCase() === design.size) || sizeOptions.find((s) => s.name === 'Fixed'),
                                    calculateScaleFactor() * 1.3,
                                    design.shape,
                                    true
                                )}
                            >
                                {design.backside === 'different' ? renderCardContent('B') : renderCardContent('A')}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* Fullscreen Blocking Add-to-Cart Loader Spinner */}
            {loading && createPortal(
                <div
                    className="global-blocking-loader"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999999,
                        pointerEvents: 'all'
                    }}
                >
                    <div className="global-loading-spinner" style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #2563eb',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '16px'
                    }} />
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827', fontFamily: 'sans-serif' }}>
                        {t('adding_to_cart', 'Adding to cart...')}
                    </span>
                </div>,
                document.body
            )}
        </div>
    );
}
